/**
 * Plot Matrix Category Generator
 *
 * This utility generates Plot Matrix columns based on CATEGORIES instead of individual fields.
 * Each category aggregates multiple related fields into a single column.
 */

import { generateBOQSummaryWithCategories } from './boqSummaryGenerator';
import {
  STEP_CATEGORIES,
  PLOT_MATRIX_STEP_CATEGORIES,
  CUT_FILL_CATEGORIES,
  PLOT_MATRIX_SITE_STRIP_CATEGORIES,
  PLOT_MATRIX_CUT_FILL_CATEGORIES,
  FOUNDATION_CATEGORIES,
  GARDEN_CATEGORIES,
  PLOT_DRIVES_CATEGORIES,
  PLOT_PAVINGS_CATEGORIES,
  PLOT_MATRIX_GARDEN_CATEGORIES,
  SCAFFOLD_CATEGORIES,
} from './billSummaryCategories';
import { transformPlotDrivesData, reconstructPlotDrivesColumnHeaders } from './plotDrivesColumnMapping';
import { reconstructPlotPavingsColumnHeaders } from './plotPavingsColumnMapping';
import { resolveScreenWallsCode, resolveEOPillarCode } from './retainingWallsReferenceMapping';
import { RateMappingJSON } from '../types/rateMappingTypes';
import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { createCutFillsRateLookupMap, applyCutFillsRatesToSummary } from './cutFillsItemLibraryIntegration';
import { createFoundationsRateLookupMap, applyFoundationsRatesToSummary } from './foundationsItemLibraryIntegration';
import { createDrainageRateLookupMap, applyDrainageRatesToSummary } from './drainageItemLibraryIntegration';
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from './drainageReferenceMapping';
import { createPlotDrivesRateLookupMap, applyPlotDrivesRatesToSummary } from './plotDrivesItemLibraryIntegration';
import { createPlotServiceRateLookupMap, applyPlotServiceRatesToSummary } from './plotServiceItemLibraryIntegration';
import { createPlotPavingsRateLookupMap, applyPlotPavingsRatesToSummary } from './plotPavingsItemLibraryIntegration';
import { createScaffoldRateLookupMap, applyScaffoldRatesToSummary } from './scaffoldItemLibraryIntegration';
import { createGardenRateLookupMap, applyGardenRatesToSummary } from './gardenItemLibraryIntegration';
import { createRetainingWallsRateLookupMap, applyRetainingWallsRatesToSummary } from './retainingWallsItemLibraryIntegration';
import {
  calculateDrainageFoulTotal,
  calculateDrainageStormTotal,
  calculatePlotServicesSectionTotal,
  calculateServiceConnectionsGrandTotal,
} from './summaryCalculations';
import { wallFieldToSpecId } from '../mock/walls-section-build-up';
import { geoToberWallFieldToSpecId } from '../mock/geo-tober-wall-build-up';
import {
  computeRetainingWallSpecAmounts,
  computeGeoToberWallSpecAmounts,
  type WallBuildUpDimensionItem,
} from './wallBuildUpCollectiveAmounts';

interface GroundworksStepData {
  cutFillsTable?: any[];
  foundationsTable?: any[];
  drainageTable?: any[]; // Legacy field for backward compatibility
  drainageTableStandard?: any[]; // Primary drainage field
  plotDrivesTable?: any[];
  plotServiceTable?: any[];
  plotPavingsTable?: any[];
  scaffoldTable?: any[];
  // Scaffold dynamic dimension values — also carries the "Geotextile Layer"
  // checkbox per tab, as "true"/"false" under keys scaffoldGeotextileEnabled /
  // loadingGeotextileEnabled / stairGeotextileEnabled.
  scaffoldDimensionValues?: Record<string, string>;
  gardenTable?: any[];
  retainingWallsTable?: any[];
  geoToberWallTable?: any[];
  externalWorksTable?: any[];
  // Wall Section Build Up data — keyed by spec ID, value is saved build-up items
  wallsBuildUpData?: Record<string, any[]>;
  geoToberWallBuildUpData?: Record<string, any[]>;
  // Imported dimension data — keyed by spec ID, value is items with real net values from Excel
  wallsBuildUpDimensions?: Record<string, Array<{ description: string; net: number; unit: string; id?: string }>>;
  geoToberWallBuildUpDimensions?: Record<string, Array<{ description: string; net: number; unit: string; id?: string }>>;
}

/**
 * Plot Matrix cell-calculation strategy. Determines how each cell is derived
 * from per-row qty + per-field rate:
 *
 *   • `proportional` — `(rowQty / totalQty) × Math.round(totalQty) × displayRate`.
 *     Proportional Bill Summary share — each row's cell is its share of the
 *     field's BoQ amount (`Math.round(totalQty) × displayRate`). Cell sums
 *     across a field reconcile to the Sum Analysis section total exactly,
 *     since `Σ_rows (rowQty / totalQty) × billSummaryAmount = billSummaryAmount`.
 *
 *   • `rawRowQty` — `rowQty × displayRate`. Literal per-row math with no
 *     rounding of `rowQty`. Cell sums may drift from the BoQ section total
 *     by sub-pence per row (BoQ rounds the column total qty before applying
 *     rate; raw-row mode rounds nothing).
 *
 *   • `roundedRowQty` — `Math.round(rowQty) × displayRate`. Literal per-row
 *     math with each row's qty rounded individually before multiplying.
 *     Cell sums can differ from BoQ when rounded row qtys don't sum to the
 *     column's rounded total qty.
 *
 * Garden additionally divides by the field's `occ` (occurrence count across
 * duplicate Plot Matrix Garden columns) so the per-column sum identity
 * `Σ_columns (fullAmount / occ) = fullAmount` still holds in every mode.
 * Scaffold dispatches on the per-category platform qty + 2dp `combinedRate`
 * already used by the BoQ consolidation.
 */
export type CellCalcStrategy = 'proportional' | 'rawRowQty' | 'roundedRowQty';

export const DEFAULT_CELL_CALC_STRATEGY: CellCalcStrategy = 'proportional';

// Maps a section key (e.g. 'Foundations') to its expected uplifted BoQ total.
// Multiple stepNames may share the same section key (see `getColumnSectionKey`).
export type BoqStepTargets = Map<string, number>;

// Maps column stepName to a canonical section key used as the BoqStepTargets
// key.  Steps belonging to the same BoQ calculation (e.g. the three Plot
// Drives step variants) share one section key so their combined cell total is
// reconciled against a single target.
function getColumnSectionKey(stepName: string): string {
  if (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') return 'Plot Drives';
  if (stepName === 'Plot Service' || stepName === 'Service Connections') return 'Plot Service';
  if (stepName === 'Ret & Screen Walls' || stepName === 'Geowall & Tobermore Walls') return 'Ret & Screen Walls';
  return stepName;
}

/**
 * Sub Stage category names where the cell should display "Included" in the Plot Matrix.
 * Derived from PLOT_MATRIX_STEP_CATEGORIES: any category whose fields list is exactly ["included"].
 * Display-only — underlying numeric value remains 0 for all calculations, totals, and exports.
 */
export const INCLUDED_SUB_STAGES = new Set<string>(
  Object.values(PLOT_MATRIX_STEP_CATEGORIES).flatMap((stepCategories) =>
    stepCategories
      ? Object.entries(stepCategories)
          .filter(([, fields]) => fields.length === 1 && fields[0] === 'included')
          .map(([categoryName]) => categoryName)
      : []
  )
);

export interface PlotMatrixCategoryColumn {
  key: string;
  stepName: string;
  categoryName: string;
  fieldNames: string[];
  hasData: boolean;
  dataIndex: string;
}

export interface PlotMatrixRow {
  key: string;
  plotNo: string;
  housetype: string;
  rowType: 'plot' | 'garage'; // Distinguishes plot rows from garage rows
  [key: string]: any; // Dynamic category totals
}

/** One field's contribution to a Plot Matrix cell value. */
export interface CellBreakdownField {
  fieldName: string;
  /** Item-library resource code — used by the renderer to look up dbRate. */
  resourceCode: string;
  rowQty: number;
  totalQty: number;
  billSummaryAmount: number;
  /** The rate already applied in the BoQ computation (= dbRate × thickness multiplier etc.). */
  modifiedRate: number;
  contribution: number;
  /**
   * For wall build-up items only: the BoQ `net` quantity per linear metre of wall.
   * When set, `rowQty = rowMetres × net`, so the true "per-metre" multiplier is
   * `net × (modifiedRate / dbRate)` rather than `modifiedRate / dbRate`.
   */
  net?: number;
}

/**
 * Dev-mode per-cell breakdown stored on `cell._breakdown`.  Only set for
 * sections that use the standard proportional / rawRowQty / roundedRowQty
 * formula (Foundations, Garden, Cut & Fill, Plot Drives, Plot Pavings,
 * Scaffold, Drainage, Plot Service / Service Connections).
 */
export interface CellBreakdown {
  fields: CellBreakdownField[];
  strategy: CellCalcStrategy;
  preUpliftTotal: number;
}

/**
 * Extract category columns with values from a table
 */
const extractCategoryColumns = (
  table: any[] | undefined,
  stepName: string,
  stepKey: string
): PlotMatrixCategoryColumn[] => {
  // Every Plot Matrix valuation stage must render even when its source table
  // is empty — the matrix iterates `cutFillsTable` for plot rows, so a tender
  // with Cut & Fill entered but other stages not yet entered still needs all
  // their columns visible (with £0 cells). Listing every step explicitly
  // (rather than `true`) so any future step added to `generatePlotMatrixCategoryColumns`
  // makes a conscious decision about visibility.
  const isForceShowStep =
    stepName === 'Cut & Fills' ||
    stepName === 'Foundations' ||
    stepName === 'Scaffold' ||
    stepName === 'Drainage' ||
    stepName === 'Plot Service' ||
    stepName === 'Plot Drives' ||
    stepName === 'Ret & Screen Walls' ||
    stepName === 'Service Connections' ||
    stepName === 'Plot Pavings' ||
    stepName === 'Garden' ||
    stepName === 'Private Drives Surface' ||
    stepName === 'Shared Drives Surface';

  const hasTableRows = !!table && table.length > 0;
  if (!hasTableRows && !isForceShowStep) return [];

  // Transform Plot Drives data from column format to semantic format
  let transformedTable: any[] = hasTableRows ? (table as any[]) : [];
  if (
    hasTableRows &&
    (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface')
  ) {
    transformedTable = transformPlotDrivesData(transformedTable);
  }

  // Use Plot Matrix specific categories for column display
  const categories = PLOT_MATRIX_STEP_CATEGORIES[stepName];
  if (!categories) return [];

  const columns: PlotMatrixCategoryColumn[] = [];
  const excludedFields = ["key", "id", "_id", "createdAt", "updatedAt", "plotNo", "housetype", "foundationType", "filename", "sheetName", "columns", "data", "onImport", "skipValidation"];

  Object.entries(categories).forEach(([categoryName, expectedFields]) => {
    // Find which fields from this category actually exist in the table with
    // data. `sampleRow` may be undefined for force-show steps with an empty
    // table — in that case `availableFields` stays empty and the column is
    // still emitted via the force-show branch below.
    const sampleRow = transformedTable[0];
    const availableFields: string[] = [];

    // Debug logging for Plot Pavings, Scaffold, Ret & Screen Walls, Geowall & Tobermore Walls, and Foundations categories
    if (stepName === "Plot Pavings" || stepName === "Scaffold" || stepName === "Ret & Screen Walls" || stepName === "Geowall & Tobermore Walls" || stepName === "Foundations") {
     
    }

    // Check each expected field name
    expectedFields.forEach((expectedFieldName) => {
      // No rows yet — fall through to the force-show branch below with
      // empty `availableFields`.
      if (!sampleRow) return;
      // Skip if field doesn't exist in the table
      if (!(expectedFieldName in sampleRow)) {
        if (stepName === "Plot Pavings" || stepName === "Scaffold" || stepName === "Ret & Screen Walls" || stepName === "Geowall & Tobermore Walls" || stepName === "Foundations") {
        }
        return;
      }

      // Only include NUMERIC fields
      const sampleValue = sampleRow[expectedFieldName];
      const isNumericField =
        typeof sampleValue === "number" ||
        (!isNaN(parseFloat(sampleValue)) && sampleValue !== "");

      if (!isNumericField) {
        if (stepName === "Plot Pavings" || stepName === "Scaffold" || stepName === "Ret & Screen Walls" || stepName === "Geowall & Tobermore Walls" || stepName === "Foundations") {
        }
        return;
      }

      // Check if ANY row in the table has a non-zero value for this field
      const hasData = transformedTable.some((row) => {
        const value = row[expectedFieldName];
        if (value === undefined || value === null) return false;
        const numValue = typeof value === "number" ? value : parseFloat(value);
        return !isNaN(numValue) && numValue !== 0;
      });

      if (hasData) {
        availableFields.push(expectedFieldName);
        if (stepName === "Plot Pavings" || stepName === "Scaffold" || stepName === "Ret & Screen Walls" || stepName === "Geowall & Tobermore Walls" || stepName === "Foundations") {
        }
      } else {
        if (stepName === "Plot Pavings" || stepName === "Scaffold" || stepName === "Ret & Screen Walls" || stepName === "Geowall & Tobermore Walls" || stepName === "Foundations") {
        }
      }
    });

    // Skip "Included" sentinel categories — they carry no data and must not appear as columns
    const isIncludedSentinel = expectedFields.length === 1 && expectedFields[0] === 'included';
    if (isIncludedSentinel) return;

    // Always show categories even without data for force-show steps, and for
    // any category whose expected field list is empty (placeholder sub-stages).
    const forceShowCategories = isForceShowStep || expectedFields.length === 0;

    // Only create column if we have at least one field with data OR it's a force-show category
    if (availableFields.length > 0 || forceShowCategories) {
      const dataIndex = `${stepKey}_${categoryName.replace(/[^a-zA-Z0-9]/g, '_')}`;

    

      columns.push({
        key: `${stepName}-${categoryName}`,
        stepName,
        categoryName,
        fieldNames: availableFields.length > 0 ? availableFields : expectedFields,
        hasData: availableFields.length > 0,
        dataIndex,
      });
    }
  });

  return columns;
};

/**
 * Generate Plot Matrix Category Columns
 */
export const generatePlotMatrixCategoryColumns = (
  groundworksData: GroundworksStepData
): PlotMatrixCategoryColumn[] => {
  const allColumns: PlotMatrixCategoryColumn[] = [];

  // ORDER MATTERS: This array defines the column order in Plot Matrix
  const steps = [
    {
      data: groundworksData.cutFillsTable,
      name: "Cut & Fills",
      key: "cutFills",
    },
    {
      data: groundworksData.foundationsTable,
      name: "Foundations",
      key: "foundations",
    },
    {
      data: groundworksData.scaffoldTable,
      name: "Scaffold",
      key: "scaffold"
    },
    {
      data: groundworksData.drainageTableStandard || groundworksData.drainageTable,
      name: "Drainage",
      key: "drainage"
    },
    {
      data: groundworksData.plotServiceTable,
      name: "Plot Service",
      key: "plotService",
    },
    {
      data: groundworksData.plotDrivesTable,
      name: "Plot Drives",
      key: "plotDrives",
    },
    {
      // For column detection, merge retainingWallsTable rows with geoToberWallTable rows
      // so that Geo/Tobermore fields (geoWallGPCW600, tpcw1, etc.) are visible alongside
      // GRW/DRW/CCRW/screenWalls* fields when determining which columns have data.
      data: (() => {
        const retRows = groundworksData.retainingWallsTable || [];
        const geoRows = groundworksData.geoToberWallTable || [];
        if (retRows.length === 0 && geoRows.length === 0) return undefined;
        // Merge by plotNo: for each retaining walls row, overlay the matching geo-tober row
        const merged = retRows.map((retRow: any) => {
          const geoRow = geoRows.find((g: any) => g.plotNo === retRow.plotNo) || {};
          return { ...retRow, ...geoRow };
        });
        // Append any geo-tober rows whose plotNo has no match in retainingWallsTable
        geoRows.forEach((geoRow: any) => {
          if (!retRows.some((r: any) => r.plotNo === geoRow.plotNo)) {
            merged.push({ ...geoRow });
          }
        });
        return merged.length > 0 ? merged : undefined;
      })(),
      name: "Ret & Screen Walls",
      key: "retainingWalls",
    },
    {
      data: groundworksData.plotServiceTable,
      name: "Service Connections",
      key: "serviceConnections",
    },
    {
      data: groundworksData.plotPavingsTable,
      name: "Plot Pavings",
      key: "plotPavings",
    },
    {
      data: groundworksData.gardenTable,
      name: "Garden",
      key: "garden"
    },
    {
      data: groundworksData.plotDrivesTable,
      name: "Private Drives Surface",
      key: "privateDrivesSurface",
    },
    {
      data: groundworksData.plotDrivesTable,
      name: "Shared Drives Surface",
      key: "sharedDrivesSurface",
    },
    // POS Areas excluded - not plot-specific data
    // Geowall & Tobermore Walls excluded - not required in Plot Matrix
  ];

  steps.forEach(({ data, name, key }) => {
    const stepColumns = extractCategoryColumns(data, name, key);
    allColumns.push(...stepColumns);
  });

  return allColumns;
};

/**
 * Whether this tender's take-off data includes any garage rows (as opposed
 * to plot rows) — used by the Add/Edit Item modal's "Include in Plot
 * Matrix" → "Distribute amount across" checkboxes (useBillSummaryExtraItems.tsx,
 * DynamicBillSummaryPanel.tsx) to hide the "Garages" option entirely for a
 * tender that has none, rather than offering a checkbox that would always
 * distribute to nothing. Walks the same set of take-off tables
 * `generatePlotMatrixCategoryRows`'s own plot-identity union does — a row is
 * a garage row only when explicitly tagged `plotType === 'garage'` (absent
 * or `'plot'` is a plot row).
 */
export const hasGaragePlotsInGroundworksData = (groundworksData: GroundworksStepData): boolean => {
  const tables: Array<any[] | undefined> = [
    groundworksData.cutFillsTable,
    groundworksData.foundationsTable,
    groundworksData.scaffoldTable,
    groundworksData.gardenTable,
    groundworksData.plotDrivesTable,
    groundworksData.plotPavingsTable,
    groundworksData.plotServiceTable,
    groundworksData.drainageTableStandard || groundworksData.drainageTable,
    groundworksData.retainingWallsTable,
    groundworksData.geoToberWallTable,
  ];
  return tables.some((table) => (table || []).some((row: any) => row?.plotType === 'garage'));
};

/**
 * Whether this tender's take-off data includes any plot rows (as opposed to
 * garage rows) — the plot-side counterpart to `hasGaragePlotsInGroundworksData`
 * above, same table set and identity test: a row is a plot row unless it's
 * explicitly tagged `plotType === 'garage'` (absent or `'plot'` is a plot row).
 */
export const hasPlotRowsInGroundworksData = (groundworksData: GroundworksStepData): boolean => {
  const tables: Array<any[] | undefined> = [
    groundworksData.cutFillsTable,
    groundworksData.foundationsTable,
    groundworksData.scaffoldTable,
    groundworksData.gardenTable,
    groundworksData.plotDrivesTable,
    groundworksData.plotPavingsTable,
    groundworksData.plotServiceTable,
    groundworksData.drainageTableStandard || groundworksData.drainageTable,
    groundworksData.retainingWallsTable,
    groundworksData.geoToberWallTable,
  ];
  return tables.some((table) => (table || []).some((row: any) => row?.plotType === 'plot' || !row?.plotType));
};

/**
 * Apply a per-cell uplift factor (prelims + OH&P) to a Plot Matrix row
 * array. Walks every row, multiplies the numeric `total` on every cell
 * (`{ total: number }`) by `upliftFactor`, preserving sibling metadata
 * and separator sentinel rows. Returns the input by reference when the
 * factor is exactly 1 (no-op short-circuit). Used by both the on-screen
 * Plot Matrix (`PlotMatrix.tsx`'s `displayPlotMatrixData`) and the Excel
 * exporter so the same uplift logic runs in both code paths.
 */
export const applyPlotMatrixUplift = (
  rows: any[],
  upliftFactor: number,
  boqCanonicalGrandTotal?: number,
  // Cell-calc strategy. Only `proportional` (Option A) participates in
  // proportional residual sharing across cells (per-column Hamilton + BoQ
  // grand-total reconciliation). For `rawRowQty` (Option B) cells must stay
  // literal `rowQty × rate × uplift` rounded per cell with no cross-cell
  // adjustment — even when that means the matrix grand total drifts from
  // BoQ Sum Analysis.
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
  // Per-section reconciliation (preferred over the global `boqCanonicalGrandTotal`
  // path): maps column dataIndex → section key.  Built from the columns array by
  // `generatePlotMatrixCategoryRows` — callers that build rows without columns
  // (e.g. tests) may omit both and only per-column Hamilton runs.
  columnSectionMap?: Map<string, string>,
  // Maps section key → expected uplifted total (same source as BoQ Sum
  // Analysis).  When provided alongside `columnSectionMap`, delta for each
  // section is distributed only within that section's columns, not across the
  // whole matrix.
  boqStepTargets?: BoqStepTargets,
): any[] => {
  // Option B short-circuit: per-cell 2dp round only, no Hamilton or
  // whole-matrix reconciliation. Matches the pre-sharing behavior — see
  // memory `feedback_plot_matrix_cell_calc` (user rejects rounding-
  // adjustment rows even when totals drift from BoQ).
  if (strategy === 'rawRowQty') {
    return rows.map((row: any) => {
      if (row?._isSeparator) return row;
      const out: any = { ...row };
      Object.keys(row).forEach((key) => {
        const v = row[key];
        if (v && typeof v === 'object' && 'total' in v) {
          const uplifted = (v.total || 0) * upliftFactor;
          out[key] = { ...v, total: parseFloat(uplifted.toFixed(2)) };
        }
      });
      return out;
    });
  }

  // Per-column Hamilton (largest-remainder) rounding.
  //
  // Why this exists: with proportional-share cells, the math contract is
  // `Σ_rows (rowQty / totalQty) × billSummaryAmount = billSummaryAmount`
  // per field (and equivalently per column = Σ field amounts in that
  // column). After `× upliftFactor`, the per-column unrounded sum equals
  // the BoQ Sum Analysis section line × uplift exactly. The drift the
  // estimator sees between the Plot Matrix grand total and Sum Analysis
  // comes entirely from per-cell 2dp rounding — `Σ round2(x_i)` can
  // differ from `round2(Σ x_i)` by up to `(numberOfCells - 1) × 0.005p`,
  // which on a 50-plot × 30-field job is a few £.
  //
  // Largest-remainder distribution fixes this WITHOUT breaking the
  // "displayed cell value == stored cell value" contract: every cell still
  // ends up at an exact 2dp value, and the column-total strip equals the
  // sum of displayed cells. The trick is that a few cells per column are
  // rounded "up" 1p (the ones with the largest fractional remainder)
  // instead of strict nearest-even, so `Σ rounded cells = canonical`.
  //
  // Algorithm per column dataIndex:
  //   1. canonicalPence = round(Σ uplifted × 100)
  //   2. flooredPence[i] = floor(uplifted_i × 100)  (with FP-noise epsilon)
  //   3. residual = canonicalPence - Σ flooredPence  (always 0 ≤ residual < cells.length)
  //   4. Sort indices by fractional remainder (uplifted_i × 100 - floor) DESC
  //   5. Add 1p to the first `residual` cells in that order
  //
  // Edge cases:
  //   - Negative residual (sumFloored > canonical) shouldn't happen with
  //     floor() but is handled defensively by subtracting 1p from the
  //     smallest-remainder cells.
  //   - Cells with value 0 have remainder 0, so they sort to the bottom
  //     and never absorb residual unless every cell in the column is 0.
  //   - The Total / row-total column has no `_isSeparator` cells; those
  //     pass through untouched.
  //
  // Result for the user: Plot Matrix grand total = `Σ_columns canonical_column`
  // = `Σ_fields billSummaryAmount × upliftFactor` = BoQ Sum Analysis grand total
  // × upliftFactor, to the penny. Soil-management and Rule-C distribution
  // (Drainage / Plot Service / Service Connections) also benefit because
  // their unrounded cells sum to `boqAmount` exactly.

  // Step 1: deep-copy rows and apply the uplift unrounded, so the per-column
  // pass below operates on un-truncated floats.
  const out: any[] = rows.map((row: any) => {
    if (row?._isSeparator) return row;
    const copy: any = { ...row };
    Object.keys(row).forEach((key) => {
      const v = row[key];
      if (v && typeof v === 'object' && 'total' in v) {
        copy[key] = { ...v, total: (v.total || 0) * upliftFactor };
      }
    });
    return copy;
  });

  // Step 2: collect a cell reference list per column dataIndex.
  type CellRef = { rowIdx: number; key: string };
  const columns = new Map<string, CellRef[]>();
  out.forEach((row: any, rowIdx: number) => {
    if (row?._isSeparator) return;
    Object.keys(row).forEach((key) => {
      const v = row[key];
      if (v && typeof v === 'object' && 'total' in v) {
        if (!columns.has(key)) columns.set(key, []);
        columns.get(key)!.push({ rowIdx, key });
      }
    });
  });

  // Step 3: per-column largest-remainder rounding.
  const FP_EPSILON = 1e-9;
  columns.forEach((cells) => {
    if (cells.length === 0) return;
    const uplifted = cells.map((c) => Number(out[c.rowIdx][c.key].total) || 0);

    // Canonical column total in pence (single rounding of the unrounded sum).
    const sumUnroundedPence = uplifted.reduce((s, v) => s + v * 100, 0);
    const canonicalPence = Math.round(sumUnroundedPence);

    // Floor each cell to a whole pence, with a tiny epsilon so values that
    // are mathematically exact 2dp (e.g. 3.06) but carry FP noise like
    // 305.9999999998 still floor to 306, not 305.
    const valPence = uplifted.map((v) => v * 100);
    const flooredPence = valPence.map((p) => Math.floor(p + FP_EPSILON));
    const remainders = valPence.map((p, i) => p - flooredPence[i]);
    const sumFloored = flooredPence.reduce((s, p) => s + p, 0);
    const residual = canonicalPence - sumFloored;

    if (residual !== 0) {
      // Sort indices by fractional remainder descending — cells "closest to
      // rounding up" absorb the +1p adjustments first.
      const order = uplifted
        .map((_, i) => i)
        .sort((a, b) => remainders[b] - remainders[a]);

      if (residual > 0) {
        for (let i = 0; i < residual && i < order.length; i++) {
          flooredPence[order[i]] += 1;
        }
      } else {
        // Defensive: subtract 1p from the smallest-remainder cells.
        const reversed = order.slice().reverse();
        const toSubtract = -residual;
        for (let i = 0; i < toSubtract && i < reversed.length; i++) {
          flooredPence[reversed[i]] = Math.max(0, flooredPence[reversed[i]] - 1);
        }
      }
    }

    // Write back the final 2dp value into each cell.
    cells.forEach((c, i) => {
      out[c.rowIdx][c.key].total = flooredPence[i] / 100;
    });
  });

  // Step 4: Reconciliation against BoQ targets.
  //
  // After per-column Hamilton, each column total matches its proportional-share
  // canonical to the penny.  However the SECTION total (sum of all columns in a
  // section) can still drift from the `calculate*GrandTotal` value used by BoQ
  // Sum Analysis because the two use different rounding paths:
  //   • Per-column Hamilton uses `billSummaryAmount` (2dp per field) as the
  //     canonical for each column.
  //   • `calculate*GrandTotal` accumulates unrounded `Math.round(qty) × rate`
  //     products and rounds only at the section level.
  //
  // Two reconciliation modes:
  //   (a) Per-section (preferred): when `boqStepTargets` + `columnSectionMap`
  //       are both provided, the delta for each BoQ section is distributed only
  //       across non-zero cells in that section's columns.  This keeps the
  //       adjustment localised — a Foundations rounding penny never moves into a
  //       Drainage cell.
  //   (b) Whole-matrix fallback (legacy): when only `boqCanonicalGrandTotal` is
  //       supplied, the overall residual is spread across every non-zero cell in
  //       the matrix (original behaviour, preserved for backward compatibility).
  //
  // In both modes: cells with `total === 0` are skipped so "no data" plots keep
  // their £0.00; residual wraps around if it exceeds the non-zero cell count.
  type ReconcileCell = {
    rowIdx: number; key: string; valuePence: number; remainder: number; delta: number;
  };

  const reconcileSection = (cells: ReconcileCell[], targetPence: number) => {
    const actualPence = cells.reduce((s, c) => s + c.valuePence, 0);
    const residual = targetPence - actualPence;
    if (residual === 0 || cells.length === 0) return;

    cells.sort((a, b) =>
      residual > 0 ? b.remainder - a.remainder : a.remainder - b.remainder,
    );
    const step = residual > 0 ? 1 : -1;
    const abs = Math.abs(residual);
    for (let i = 0; i < abs; i++) {
      cells[i % cells.length].delta += step;
    }
    cells.forEach((c) => {
      if (c.delta === 0) return;
      const newPence = Math.max(0, c.valuePence + c.delta);
      out[c.rowIdx][c.key].total = newPence / 100;
    });
  };

  if (boqStepTargets && columnSectionMap && boqStepTargets.size > 0) {
    // (a) Per-section reconciliation — delta stays within the section that owns it.
    const sectionCellsMap = new Map<string, ReconcileCell[]>();

    out.forEach((row: any, rowIdx: number) => {
      if (row?._isSeparator) return;
      Object.keys(row).forEach((key) => {
        const sectionKey = columnSectionMap.get(key);
        if (!sectionKey || !boqStepTargets.has(sectionKey)) return;
        const v = row[key];
        if (!(v && typeof v === 'object' && 'total' in v)) return;
        const valuePence = Math.round((Number(v.total) || 0) * 100);
        if (valuePence === 0) return;
        const origTotal = Number(rows[rowIdx]?.[key]?.total) || 0;
        const upliftedPence = origTotal * upliftFactor * 100;
        const remainder = upliftedPence - Math.floor(upliftedPence + FP_EPSILON);
        if (!sectionCellsMap.has(sectionKey)) sectionCellsMap.set(sectionKey, []);
        sectionCellsMap.get(sectionKey)!.push({ rowIdx, key, valuePence, remainder, delta: 0 });
      });
    });

    boqStepTargets.forEach((sectionTarget, sectionKey) => {
      const cells = sectionCellsMap.get(sectionKey) ?? [];
      reconcileSection(cells, Math.round(sectionTarget * 100));
    });
  } else if (typeof boqCanonicalGrandTotal === 'number') {
    // (b) Whole-matrix fallback — spreads overall residual across every non-zero cell.
    const globalCells: ReconcileCell[] = [];

    out.forEach((row: any, rowIdx: number) => {
      if (row?._isSeparator) return;
      Object.keys(row).forEach((key) => {
        const v = row[key];
        if (!(v && typeof v === 'object' && 'total' in v)) return;
        const valuePence = Math.round((Number(v.total) || 0) * 100);
        if (valuePence !== 0) {
          const origTotal = Number(rows[rowIdx]?.[key]?.total) || 0;
          const upliftedPence = origTotal * upliftFactor * 100;
          const remainder = upliftedPence - Math.floor(upliftedPence + FP_EPSILON);
          globalCells.push({ rowIdx, key, valuePence, remainder, delta: 0 });
        }
      });
    });

    reconcileSection(globalCells, Math.round(boqCanonicalGrandTotal * 100));
  }

  return out;
};

/**
 * Build a map of evaluateCode → nettRate from the item library (flat array).
 * Shared by both wall build-up rate helpers below, and by the central-repo
 * payload builder (`resolveTaskResources` in `plotMatrixFormulaDefinitions`).
 */
export function buildEvaluateRateMap(itemLibraryData: ItemLibraryResponse | undefined): Map<string, number> {
  const rateMap = new Map<string, number>();
  const libItems = itemLibraryData as unknown as ItemLibraryItem[];
  if (Array.isArray(libItems)) {
    libItems.forEach((lib) => {
      const code = lib.resourceCode?.trim().toUpperCase();
      if (code) rateMap.set(code, lib.nettRate || lib.baseRate || 0);
    });
  }
  return rateMap;
}

// Per-spec net-per-metre / collective amount derivation for both wall
// families now lives in `computeRetainingWallSpecAmounts` /
// `computeGeoToberWallSpecAmounts` (wallBuildUpCollectiveAmounts.ts) — the
// SAME calculation `RetScreenWallsBillSummary.tsx` / `GEOToberWallBillSummary.tsx`
// use for their Collective view, so Plot Matrix can no longer drift from what
// those Bill Summaries display by maintaining a separate implementation here
// (see `calculateCategoryTotalForRow`'s "Retaining Walls - Complete" branch below).

/**
 * Get BOQ rates for fields in a category - exactly matches BOQ calculation
 */
function getBOQFieldRates(
  groundworksData: GroundworksStepData,
  stepName: string,
  categoryName: string,
  fieldNames: string[],
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse
): Map<string, number> {
  const fieldRates = new Map<string, number>();

  const stepCategoryMap: { [key: string]: any } = {
    'Foundations': STEP_CATEGORIES['Foundations'],
    'Cut & Fills': STEP_CATEGORIES['Cut & Fills'],
    'Drainage': STEP_CATEGORIES['Drainage'],
    'Plot Drives': STEP_CATEGORIES['Plot Drives'],
    'Plot Service': STEP_CATEGORIES['Plot Service'],
    'Plot Pavings': STEP_CATEGORIES['Plot Pavings'],
    'Scaffold': STEP_CATEGORIES['Scaffold'],
    'Garden': STEP_CATEGORIES['Garden'],
    // POS Areas excluded - not plot-specific data
    'Ret & Screen Walls': STEP_CATEGORIES['Ret & Screen Walls'],
    'Service Connections': STEP_CATEGORIES['Plot Service'], // Uses same categories as Plot Service
    // Geowall & Tobermore Walls excluded - not required in Plot Matrix
    'Private Drives Surface': PLOT_MATRIX_STEP_CATEGORIES['Private Drives Surface'],
    'Shared Drives Surface': PLOT_MATRIX_STEP_CATEGORIES['Shared Drives Surface'],
  };

  const categories = stepCategoryMap[stepName];
  if (!categories) return fieldRates;

  try {
    // Get dimension values for steps that need them (same as GenericBillSummary)
    const dimensionValues = (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface')
      ? (groundworksData as any)?.plotDrivesDimensionValues
      : stepName === 'Plot Pavings'
      ? (groundworksData as any)?.plotPavingsDimensionValues
      : stepName === 'Scaffold'
      ? (groundworksData as any)?.scaffoldDimensionValues
      : stepName === 'Garden'
      ? (groundworksData as any)?.gardenDimensionValues
      // POS Areas excluded - not plot-specific data
      : undefined;

    // Get column headers for steps that need them (for rate lookup based on dynamic headers)
    const columnHeaders = (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface')
      ? reconstructPlotDrivesColumnHeaders(
          (groundworksData as any)?.plotDrivesColumnHeaders || {},
          (groundworksData as any)?.plotDrivesDimensionValues || {}
        )
      : stepName === 'Plot Pavings'
      ? reconstructPlotPavingsColumnHeaders(
          (groundworksData as any)?.plotPavingsColumnHeaders || {},
          (groundworksData as any)?.plotPavingsDimensionValues || {}
        )
      : stepName === 'Scaffold'
      ? (groundworksData as any)?.scaffoldColumnHeaders
      : stepName === 'Garden'
      ? (groundworksData as any)?.gardenColumnHeaders
      // POS Areas excluded - not plot-specific data
      : undefined;

   

    // Special handling for Cut & Fills - use item library rates
    if (stepName === 'Cut & Fills' && itemLibraryData) {

      // Generate BOQ items without rates
      const boqItems = generateBOQSummaryWithCategories(groundworksData, stepName, categories, dimensionValues, null, columnHeaders);

      // Create rate lookup map from item library
      const rateLookupMap = createCutFillsRateLookupMap(itemLibraryData);

      // Apply item library rates
      const itemsWithRates = applyCutFillsRatesToSummary(boqItems, rateLookupMap);

      // Extract rates for requested fields
      itemsWithRates.forEach(item => {
        if (fieldNames.includes(item.fieldName)) {
          fieldRates.set(item.fieldName, item.rate);
        }
      });

      return fieldRates;
    }

    let boqItems = generateBOQSummaryWithCategories(groundworksData, stepName, categories, dimensionValues, ratesMapping, columnHeaders);

    // Apply item library rates for each step — mirrors the logic in summaryCalculations.ts
    if (itemLibraryData) {
      let rateLookupMap: Map<string, any> = new Map();
      let applyRatesFunction: ((items: any[], map: Map<string, any>) => any[]) | null = null;

      switch (stepName) {
        case 'Foundations':
          rateLookupMap = createFoundationsRateLookupMap(itemLibraryData, groundworksData);
          applyRatesFunction = applyFoundationsRatesToSummary;
          break;
        case 'Drainage':
          rateLookupMap = createDrainageRateLookupMap(itemLibraryData);
          applyRatesFunction = applyDrainageRatesToSummary;
          break;
        case 'Plot Drives':
        case 'Private Drives Surface':
        case 'Shared Drives Surface':
          rateLookupMap = createPlotDrivesRateLookupMap(itemLibraryData, groundworksData);
          applyRatesFunction = applyPlotDrivesRatesToSummary;
          break;
        case 'Plot Service':
        case 'Service Connections':
          rateLookupMap = createPlotServiceRateLookupMap(itemLibraryData);
          applyRatesFunction = applyPlotServiceRatesToSummary;
          break;
        case 'Plot Pavings':
          rateLookupMap = createPlotPavingsRateLookupMap(itemLibraryData, groundworksData);
          applyRatesFunction = applyPlotPavingsRatesToSummary;
          break;
        case 'Scaffold':
          rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData);
          applyRatesFunction = applyScaffoldRatesToSummary;
          break;
        case 'Garden':
          rateLookupMap = createGardenRateLookupMap(itemLibraryData, groundworksData);
          applyRatesFunction = applyGardenRatesToSummary;
          break;
        case 'Ret & Screen Walls':
          rateLookupMap = createRetainingWallsRateLookupMap(
            itemLibraryData,
            (groundworksData as any)?.wallsBuildUpConfig?.screenWallsLabel,
            (groundworksData as any)?.wallsBuildUpConfig?.eoPillarLabel,
          );
          applyRatesFunction = applyRetainingWallsRatesToSummary;
          break;
      }

      if (applyRatesFunction) {
        boqItems = applyRatesFunction(boqItems, rateLookupMap);
      }
    }

    // Extract rates for requested fields — match by field name only
    boqItems.forEach(item => {
      if (fieldNames.includes(item.fieldName)) {
        fieldRates.set(item.fieldName, item.rate);
      }
    });
  } catch (error) {
  }

  return fieldRates;
}

/**
 * Generate Plot Matrix Rows with category totals
 */
/**
 * Calculate category total for a single data row (plot or garage) using BOQ rates.
 * Shared by both the plot pass and the garage pass.
 *
 * @param rowType - 'plot' | 'garage': used to isolate the rate-discovery table so
 *   that plot rates are derived from plot-only rows and garage rates from garage-only
 *   rows. This prevents the two row sets from influencing each other's calculations.
 */
function calculateCategoryTotalForRow(
  rowData: any,
  stepName: string,
  categoryName: string,
  fieldNames: string[],
  groundworksData: GroundworksStepData,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  rowType: 'plot' | 'garage' = 'plot',
  ohpPercent: number = 0
): number {
  // For Foundations and Scaffold, isolate the groundworksData snapshot to only
  // the rows matching rowType so that BOQ rate discovery (field presence, column
  // detection) is driven purely by that row set.
  let rateDiscoveryData: GroundworksStepData = groundworksData;

  if (stepName === 'Foundations' || stepName === 'Scaffold') {
    const tableKey = stepName === 'Foundations' ? 'foundationsTable' : 'scaffoldTable';
    const fullTable: any[] = (groundworksData as any)[tableKey] || [];
    const filteredTable = fullTable.filter((r) =>
      rowType === 'garage' ? r.plotType === 'garage' : (r.plotType === 'plot' || !r.plotType)
    );
    rateDiscoveryData = { ...groundworksData, [tableKey]: filteredTable };
  }

  const boqFieldRates = getBOQFieldRates(rateDiscoveryData, stepName, categoryName, fieldNames, ratesMapping, itemLibraryData);
  let categoryTotal = 0;

  if (stepName === 'Ret & Screen Walls' && categoryName === 'Screen Walls - upto DPC') {
    // ─── Screen Walls — Foundation ────────────────────────────────────────────
    // screenWalls (m) × GWALPGW003, screenWallsEO (Nr) × GWALPGW003A,
    // plus the additional excavation/concrete item-library lookups (moved
    // here from "Retaining Walls - Complete" so they group with the rest of
    // the screen wall foundation items).
    // No OHP applied here — `calculateRetScreenWallsGrandTotal` (Sum Analysis
    // section total) sums these fields at the base rate too, and Sum Analysis
    // applies OHP separately via its checkbox. Plot Matrix must match the
    // BoQ Sum Analysis grand total, so it stays at base.
    const evaluateRateMap = buildEvaluateRateMap(itemLibraryData);
    void ohpPercent;
    const screenWallsQty = rowData.screenWalls || 0;
    const screenWallsEOQty = rowData.screenWallsEO || 0;
    const screenWallsLabel = (groundworksData as any)?.wallsBuildUpConfig?.screenWallsLabel;
    const eoPillarLabel = (groundworksData as any)?.wallsBuildUpConfig?.eoPillarLabel;
    categoryTotal =
      (screenWallsQty  * (evaluateRateMap.get(resolveScreenWallsCode(screenWallsLabel))  || 0)) +
      (screenWallsEOQty * (evaluateRateMap.get(resolveEOPillarCode(eoPillarLabel)) || 0)) +
      ((rowData.screenWallsAdditionalExcavation || 0) * (evaluateRateMap.get('GWALPGW0026') || 0)) +
      ((rowData.screenWallsAdditionalConcrete || 0)   * (evaluateRateMap.get('GWALPGW0015') || 0));

  } else if (stepName === 'Ret & Screen Walls' && categoryName === 'Retaining Walls - Complete') {
    // ─── Retaining Walls — Foundation ─────────────────────────────────────────
    // No OHP here either — `calculateRetScreenWallsGrandTotal` /
    // `calculateGEOToberWallGrandTotal` return base amounts, so Plot Matrix
    // stays at base to keep the grand total in lockstep with Sum Analysis.
    const evaluateRateMap = buildEvaluateRateMap(itemLibraryData);

    // Part 1 — individual screen wall item fields × direct item library rates
    // (base only — see comment above).
    //   screenWallsSof (SoE 450mm, m)       → GWALPGW0043
    //   screenWallsLog (Log 1200mm, m)      → ACL012A
    //   screenWallsGravelBoard (Gravel board, m) → GWALPGW0023
    // (screenWallsAdditionalExcavation/Concrete moved to the "Screen Walls -
    // upto DPC" branch above.)
    const part1 =
      ((rowData.screenWallsSof || 0)         * (evaluateRateMap.get('GWALPGW0043') || 0)) +
      ((rowData.screenWallsLog || 0)         * (evaluateRateMap.get('ACL012A')     || 0)) +
      ((rowData.screenWallsGravelBoard || 0) * (evaluateRateMap.get('GWALPGW0023')  || 0));
    categoryTotal += part1;

    // Part 2 — proportional Bill Summary share (base, no OHP) across every
    // retaining-wall family so the Plot Matrix Retaining Walls column total
    // ≡ BoQ Sum Analysis Ret & Screen Walls + Geo/Tobermore section totals
    // to the penny:
    //   billSummaryAmount[spec] = Math.round(totalMetres[spec]) × parseFloat(netPerM[spec].toFixed(2))
    //   cellContribution[row][spec] = (rowMetres / totalMetres) × billSummaryAmount[spec]
    //   Σ_rows cellContribution[*][spec] = billSummaryAmount[spec]   ✓
    // GRW/DRW/CCRW quantities live on `retainingWallsTable` (iterated rows);
    // GPCW/TPCW quantities live on `geoToberWallTable` (joined by plotNo
    // via `normalizePlotNoKey` so `P-1` ≡ `1`). OHP is NOT applied here —
    // Sum Analysis applies OHP separately via its checkbox on the section
    // total, so Plot Matrix stays at base to keep grand totals reconciled.
    const wallsBuildUpData = (groundworksData as any).wallsBuildUpData as Record<string, any[]> || {};
    const wallsBuildUpDimensions = (groundworksData as any).wallsBuildUpDimensions as Record<string, WallBuildUpDimensionItem[]> | undefined;
    const geoToberBuildUpData = (groundworksData as any).geoToberWallBuildUpData as Record<string, any[]> || {};
    const geoToberWallBuildUpDimensions = (groundworksData as any).geoToberWallBuildUpDimensions as Record<string, WallBuildUpDimensionItem[]> | undefined;
    const facingBrickworkLabel = (groundworksData as any)?.wallsBuildUpConfig?.facingBrickworkLabel;

    // Same per-spec collective amount calculation `RetScreenWallsBillSummary.tsx`
    // / `GEOToberWallBillSummary.tsx` use for their Collective view — Plot
    // Matrix no longer derives its own netPerM/rate here, so it can't silently
    // drift from what those Bill Summaries display. `markupFactor = 1` (base,
    // no OH&P/Preliminaries) since Plot Matrix applies its own uplift
    // uniformly afterward (`applyPlotMatrixUplift`).
    const retSpecAmounts = computeRetainingWallSpecAmounts(
      groundworksData.retainingWallsTable,
      wallsBuildUpData,
      wallsBuildUpDimensions,
      evaluateRateMap,
      facingBrickworkLabel,
      1,
    );
    const geoSpecAmounts = computeGeoToberWallSpecAmounts(
      groundworksData.geoToberWallTable,
      geoToberBuildUpData,
      geoToberWallBuildUpDimensions,
      evaluateRateMap,
      1,
    );

    const plotKey = normalizePlotNoKey(rowData.plotNo);
    const geoToberPlotRow = (groundworksData.geoToberWallTable || []).find(
      (r: any) => normalizePlotNoKey(r.plotNo) === plotKey,
    ) || {};

    const wallSources: Array<{
      fieldMap: Record<string, string>;
      plotRow: any;
      specAmounts: Map<string, { wallQtyM: number; billSummaryAmount: number }>;
    }> = [
      {
        fieldMap: wallFieldToSpecId,
        plotRow: rowData,
        specAmounts: retSpecAmounts,
      },
      {
        fieldMap: geoToberWallFieldToSpecId,
        plotRow: geoToberPlotRow,
        specAmounts: geoSpecAmounts,
      },
    ];

    wallSources.forEach(({ fieldMap, plotRow, specAmounts }) => {
      Object.entries(fieldMap).forEach(([fieldName, specId]) => {
        const rowMetres = Number(plotRow[fieldName]) || 0;
        if (rowMetres === 0) return;
        const amt = specAmounts.get(specId);
        if (!amt || amt.wallQtyM === 0) return;
        categoryTotal += (rowMetres / amt.wallQtyM) * amt.billSummaryAmount;
      });
    });

  } else {
    // Generic calculation for all other steps / categories
    fieldNames.forEach((fieldName) => {
      const value = rowData[fieldName];
      if (value !== undefined && value !== null) {
        const quantity = typeof value === 'number' ? value : parseFloat(value) || 0;
        const rate = boqFieldRates.get(fieldName) || 0;
        categoryTotal += quantity * rate;
      }
    });
  }

  return categoryTotal;
}

export interface SoilManagementBoQTotals {
  topsoilAmount: number;       // BoQ Section 3.1 total (legacy — kept for back-compat callers)
  subsoilAmount: number;       // BoQ Section 3.2 total (legacy — kept for back-compat callers)
  subsoilManagement: string;   // 'Surplus' | 'Deficit' | '' (from Configure Headers)
  // Deviation 3 — explicit disposal/import split based on the sign of C22.
  // Only one of the two amounts is non-zero in any well-formed take-off.
  disposalAmount: number;      // Active when foundations balance C22 > 0 (Surplus)
  importAmount: number;        // Active when foundations balance C22 < 0 (Deficit)
}

/**
 * Compute Section 3.1 (Topsoil) and 3.2 (Subsoil) BoQ totals for Plot Matrix distribution.
 * Mirrors the same calculation logic used in PlotMatrix.tsx and GroundworksCutFillBillSummary.tsx.
 *
 * Deviation 3 fix: the Disposal/Import balance is now restricted to the
 * **foundations balance only** (`foundationsCutArisings − foundationsFill`),
 * not the 8-area-pair Σ used previously. The result is signed so the calling
 * code can split between the Disposal-of-Surplus and Import-of-Deficit
 * Plot Matrix columns from the sign of C22 alone:
 *   C22 > 0 → Disposal active, Import = 0
 *   C22 < 0 → Import active, Disposal = 0
 *   C22 = 0 → both zero
 * The rate source is unchanged (item-library lookup via `subsoilMgmt.code`),
 * per the project decision to defer the user-entered-rate work.
 */
export function computeSoilBoQTotals(
  groundworks: any,
  itemLibraryData: ItemLibraryResponse | undefined,
): SoilManagementBoQTotals {
  const codeRateMap = new Map<string, number>();
  const items: any[] = Array.isArray(itemLibraryData)
    ? itemLibraryData
    : ((itemLibraryData as any)?.items || []);
  items.forEach((item: any) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) codeRateMap.set(code, item.nettRate || item.baseRate || 0);
  });

  // --- Section 3.1: Topsoil ---
  const topsoilMgmt = groundworks?.topsoilManagement;
  const soilData = groundworks?.soilManagementData;
  const cutFillsTable: any[] = groundworks?.cutFillsTable || [];
  const dataRows = cutFillsTable.filter(
    (r: any) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total',
  );
  const totalOnsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
  const totalOffsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);
  // Use user-edited soilManagementData values when present
  const soilTopsoilOnsite = soilData?.topsoilOnsite !== undefined ? Number(soilData.topsoilOnsite) : totalOnsite;
  const soilTopsoilOffsite = soilData?.topsoilOffsite !== undefined ? Number(soilData.topsoilOffsite) : totalOffsite;
  const topsoilQty = Math.abs(soilTopsoilOnsite - soilTopsoilOffsite);
  const topsoilCode = topsoilMgmt?.code?.trim().toUpperCase() || '';
  const topsoilIsExcluded = !topsoilCode || topsoilCode === 'NONE';
  const topsoilRate = topsoilIsExcluded ? 0 : (codeRateMap.get(topsoilCode) ?? 0);
  const topsoilAmount = topsoilIsExcluded ? 0 : topsoilQty * topsoilRate;

  // --- Section 3.2: Subsoil (8-area-pair sum, BoQ-matching) ---
  // Reverted from the deviation report's foundations-only restriction so the
  // Plot Matrix subsoil amount matches the BoQ Section 3.2 line exactly. The
  // BoQ sums all 8 cut/fill area pairs into the disposal/import amount; the
  // foundations-only restriction created a gap whenever non-foundations
  // pairs were non-zero. `disposalAmount` / `importAmount` are kept for
  // back-compat but are now derived from the 8-pair sum, with the picker
  // driven by `subsoilManagement.management` ('Surplus' or 'Deficit') as
  // selected on the GW C&F Configure Headers dropdown.
  const subsoilMgmt = groundworks?.subsoilManagement;
  const soilRows = [
    { cutField: 'foundationsCutArisings', fillField: 'foundationsFill' },
    { cutField: 'slabPathsCut',           fillField: 'slabPathsFill' },
    { cutField: 'gardensCut',             fillField: 'gardensFill' },
    { cutField: 'drivesCut',              fillField: 'drivesFill' },
    { cutField: 'foundationsArisingsCut', fillField: 'foundationsArisingsFill' },
    { cutField: 'plotDrainageCut',        fillField: 'plotDrainageFill' },
    { cutField: 'wallsCut',               fillField: 'wallsFill' },
    { cutField: 'plotServicesCut',        fillField: 'plotServicesFill' },
  ];
  const subsoilTotalCut = soilRows.reduce((s, r) => s + (Number(soilData?.[r.cutField]) || 0), 0);
  const subsoilTotalFill = soilRows.reduce((s, r) => s + (Number(soilData?.[r.fillField]) || 0), 0);
  const subsoilQty = Math.abs(subsoilTotalCut - subsoilTotalFill);
  const subsoilCode = subsoilMgmt?.code?.trim().toUpperCase() || '';
  const subsoilIsExcluded = !subsoilCode || subsoilCode === 'NONE';
  const subsoilRate = subsoilIsExcluded ? 0 : (codeRateMap.get(subsoilCode) ?? 0);
  const subsoilAmount = subsoilIsExcluded ? 0 : subsoilQty * subsoilRate;

  // Picker driven by the user's Configure Headers selection (Surplus → Disposal,
  // Deficit → Import). Only one is non-zero at a time.
  const management = subsoilMgmt?.management || '';
  const disposalAmount = management === 'Surplus' ? subsoilAmount : 0;
  const importAmount   = management === 'Deficit' ? subsoilAmount : 0;

  return {
    topsoilAmount,
    subsoilAmount,
    subsoilManagement: management,
    disposalAmount,
    importAmount,
  };
}

/**
 * Strip the `P-` / `G-` prefix that `src/utils/storage.ts` adds to
 * `foundationsTable` and `scaffoldTable` rows on save (the cutFillsTable is
 * saved raw, so `cutFillsTable.plotNo = "1"` while
 * `foundationsTable.plotNo = "P-1"` after a round-trip). The Plot Matrix
 * matches plots across tables via this normalised key so the prefix mismatch
 * doesn't silently drop foundation data.
 */
export function normalizePlotNoKey(plotNo: unknown): string {
  return String(plotNo ?? '').trim().replace(/^[PG]-/i, '');
}

/**
 * Find the take-off-table row that corresponds to a given plot for a
 * given Plot Matrix step (e.g. Foundations row from foundationsTable,
 * Plot Drives row from the transformed plotDrivesTable). Returns the
 * matched row (with its native field-name keys + numeric qtys) or
 * `undefined` if no plot in that step's table matches the plotNo.
 *
 * Used by the central-repo submission flow to read per-field qtys for
 * each Plot Matrix column's task, so the payload can include the actual
 * activity quantity alongside each resource code. Mirrors the same
 * step-to-table mapping used internally by `generatePlotMatrixCategoryRows`.
 */
export function findStepPlotRow(
  groundworksData: GroundworksStepData,
  stepName: string,
  plotNo: string,
): Record<string, any> | undefined {
  const plotKey = normalizePlotNoKey(plotNo);
  const isGarage = String(plotNo).toUpperCase().startsWith('G-');
  const isPlotRow = (r: any) => {
    if (normalizePlotNoKey(r.plotNo) !== plotKey) return false;
    // For steps that mix plot and garage rows in the same table (Cut & Fills,
    // Foundations, Scaffold) we must filter by plotType so a garage plotNo
    // (G-1) doesn't accidentally return the plot row (P-1) — both normalise
    // to "1" and .find() would otherwise always return whichever row appears
    // first in the table.
    if (isGarage) return r.plotType === 'garage';
    return r.plotType === 'plot' || !r.plotType;
  };

  switch (stepName) {
    case 'Cut & Fills':
      return (groundworksData.cutFillsTable || []).find(isPlotRow);
    case 'Foundations':
      return (groundworksData.foundationsTable || []).find(isPlotRow);
    case 'Scaffold':
      return (groundworksData.scaffoldTable || []).find(isPlotRow);
    case 'Drainage':
      return (groundworksData.drainageTableStandard || groundworksData.drainageTable || []).find(
        isPlotRow,
      );
    case 'Plot Service':
    case 'Service Connections':
      return (groundworksData.plotServiceTable || []).find(isPlotRow);
    case 'Plot Drives':
    case 'Private Drives Surface':
    case 'Shared Drives Surface': {
      const transformed = transformPlotDrivesData(
        (groundworksData.plotDrivesTable || []) as any[],
      );
      return transformed.find(isPlotRow);
    }
    case 'Plot Pavings':
      return (groundworksData.plotPavingsTable || []).find(isPlotRow);
    case 'Garden':
      return (groundworksData.gardenTable || []).find(isPlotRow);
    case 'Ret & Screen Walls':
      return (groundworksData.retainingWallsTable || []).find(isPlotRow);
    default:
      return undefined;
  }
}

/**
 * Foundations: cached per-field {totalQty, billSummaryAmount} computed via the
 * same pipeline the Foundations Bill Summary uses
 * (`generateBOQSummaryWithCategories` → `applyFoundationsRatesToSummary` → the
 * Bill Summary's per-row rounding rule `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))`).
 * `totalQty` is the sum across ALL foundation rows (plots + garages) so the
 * proportional share `rowQty / totalQty` distributes the Bill Summary amount
 * exactly across the matrix rows that contributed to that quantity.
 *
 * Sum across all matrix rows for a given field = `Σ_rows (rowQty / totalQty) × billSummaryAmount = billSummaryAmount`,
 * so the matrix Foundation column total equals the Bill Summary grand total to
 * the penny. Per-plot / per-garage variation is preserved because each row's
 * cell still reflects its own qty share — a plot with no foundation data
 * contributes 0 to that category, a plot with the larger qty contributes more.
 */
function computeFoundationsFieldBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }> {
  const result = new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  if (!itemLibraryData || !groundworksData?.foundationsTable?.length) return result;

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Foundations',
    FOUNDATION_CATEGORIES,
    undefined,
    null,
  );
  const rateLookupMap = createFoundationsRateLookupMap(itemLibraryData, groundworksData);
  const items = applyFoundationsRatesToSummary(baseItems, rateLookupMap);

  items.forEach((item: any) => {
    const rawQty = item.quantity || 0;
    const roundedQty = Math.round(rawQty);
    // `rate` is the 2dp-rounded item-library rate — same value the estimator
    // sees in the build-up rate cells and the Bill Summary rate column. The
    // raw library rate can carry trailing decimals (e.g. 1.3533…) that don't
    // appear anywhere on screen, so multiplying a row qty by the raw rate
    // gives a cell value the estimator can't reproduce by hand (`3 × 1.35 = 4.05`
    // expected, but `3 × 1.3533… = 4.06` was displayed). Storing the 2dp
    // value here makes Options B and C produce `rowQty × visibleRate` and
    // keeps Option A's `billSummaryAmount` consistent with the same rate.
    const rate = parseFloat(((item.rate || 0) as number).toFixed(2));
    result.set(item.fieldName, {
      totalQty: rawQty,
      billSummaryAmount: parseFloat(Math.abs(roundedQty * rate).toFixed(2)),
      rate,
      resourceCode: rateLookupMap.get(item.fieldName)?.resourceCode ?? '',
    });
  });

  return result;
}

/**
 * Per-row Foundation cell value. Strategy controls the decomposition:
 *   • `proportional` (default): `Σ_field (rowQty[field] / totalQty[field]) × billSummaryAmount[field]`.
 *   • `rawRowQty`:               `Σ_field rowQty[field] × rate[field]` (signed).
 *   • `roundedRowQty`:           `Σ_field Math.round(rowQty[field]) × rate[field]` (signed).
 * `rate` is 2dp-rounded (the value the estimator sees), so Options B and C
 * produce cells the user can verify by hand (e.g. `3 × 1.35 = 4.05`).
 * Fields with zero total qty (proportional mode) or zero rowQty are skipped.
 */
function computeFoundationsCellAmount(
  rowData: any,
  fieldNames: string[],
  foundationsFieldData: Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  let total = 0;
  fieldNames.forEach((fieldName) => {
    const data = foundationsFieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    if (strategy === 'rawRowQty') {
      total += rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      total += Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      total += (rowQty / data.totalQty) * data.billSummaryAmount;
    }
  });
  return total;
}

/**
 * Per-category Scaffold consolidation data. `resourceCode` is the "+"-joined
 * consolidated code of the components that make up the category's single
 * rate (Level & Compact, Geotextile Layer only when its Configure Headers
 * checkbox is on, Crush 6F2, Type1) — e.g. "GWALP005+GWALP004+GWALP006" —
 * so the cell breakdown, and the register payload built from it, carries ONE
 * resource for the consolidated line instead of one per component.
 */
type ScaffoldCategoryData = {
  quantityField: string;
  totalPlatformQty: number;
  billSummaryAmount: number;
  combinedRate: number;
  resourceCode: string;
};

/**
 * Scaffold: cached per-category {quantityField, totalPlatformQty,
 * billSummaryAmount} mirroring `calculateScaffoldGrandTotal`'s consolidation
 * formula EXACTLY:
 *
 *   per category (Scaffold Platforms / Loading Bays / Stair Access):
 *     geotextileEnabled  = scaffoldDimensionValues[geotextileEnabledKey] === 'true'
 *     combinedRate       = Σ parseFloat(rate[i].toFixed(2)) for each rateField,
 *                          skipping the geotextile field when disabled
 *     billSummaryAmount  = Math.round(platformQty) × combinedRate
 *
 * The per-row Plot Matrix Scaffold cell distributes this billSummaryAmount
 * proportionally by `(rowPlatformQty / totalPlatformQty)`, so summing all
 * Plot Matrix Scaffold cells in a category yields exactly the BoQ
 * `calculateScaffoldGrandTotal` consolidated amount — penny-perfect parity
 * with Sum Analysis.
 */
function computeScaffoldCategoryBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, ScaffoldCategoryData> {
  const result = new Map<string, ScaffoldCategoryData>();
  if (!itemLibraryData || !groundworksData?.scaffoldTable?.length) return result;

  // Same SCAFFOLD_CONSOLIDATE config as `calculateScaffoldGrandTotal` in
  // `summaryCalculations.ts`. Keep in sync if the BoQ side ever changes.
  const SCAFFOLD_CONSOLIDATE: Record<string, { quantityField: string; rateFields: string[]; geotextileField: string; geotextileEnabledKey: string }> = {
    'Scaffold Platforms': {
      quantityField: 'scaffoldPlatform',
      rateFields: ['scaffoldLevelCompact', 'scaffoldGeotextileLayer', 'scaffoldCrush6F2', 'scaffoldType1'],
      geotextileField: 'scaffoldGeotextileLayer',
      geotextileEnabledKey: 'scaffoldGeotextileEnabled',
    },
    'Loading Bays': {
      quantityField: 'loadingBay4x2m',
      rateFields: ['loadingLevelCompact', 'loadingGeotextileLayer', 'loadingCrush6F2', 'loadingType1'],
      geotextileField: 'loadingGeotextileLayer',
      geotextileEnabledKey: 'loadingGeotextileEnabled',
    },
    'Stair Access': {
      quantityField: 'stairAccess3x3m',
      rateFields: ['stairLevelCompact', 'stairGeotextileLayer', 'stairCrush6F2', 'stairType1'],
      geotextileField: 'stairGeotextileLayer',
      geotextileEnabledKey: 'stairGeotextileEnabled',
    },
  };

  const dimensionValues: Record<string, string> = groundworksData.scaffoldDimensionValues || {};

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Scaffold',
    SCAFFOLD_CATEGORIES,
    undefined,
    null,
  );
  const rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData as any);
  const itemsWithRates = applyScaffoldRatesToSummary(baseItems, rateLookupMap);

  Object.entries(SCAFFOLD_CONSOLIDATE).forEach(([category, config]) => {
    const platformItem = itemsWithRates.find(
      (i: any) => i.fieldName === config.quantityField && i.category === category,
    );
    if (!platformItem) return;
    const platformQty: number = platformItem.quantity || 0;
    if (platformQty <= 0) return;

    const geotextileEnabled = dimensionValues[config.geotextileEnabledKey] === 'true';

    // Sum each field's rate AFTER rounding it to 2dp, skipping the geotextile
    // field unless its checkbox is on — matches calculateScaffoldGrandTotal /
    // the Rate Breakdown modal's Final Rate exactly.
    const combinedRate = config.rateFields.reduce((sum, fieldName) => {
      if (fieldName === config.geotextileField && !geotextileEnabled) return sum;
      const rateItem = itemsWithRates.find(
        (i: any) => i.fieldName === fieldName && i.category === category,
      );
      const fallbackRate = rateLookupMap.get(fieldName)?.rate ?? 0;
      const rate: number = rateItem?.rate ?? fallbackRate;
      return sum + parseFloat(rate.toFixed(2));
    }, 0);

    const billSummaryAmount = Math.abs(Math.round(platformQty) * combinedRate);

    // Consolidated code: same components, same order and same geotextile
    // condition as `combinedRate` above (and computeScaffoldSection's
    // `consolidatedResourceCode` in groundworksBoqSummaryComputer.ts).
    const resourceCode = config.rateFields
      .filter((fieldName) => !(fieldName === config.geotextileField && !geotextileEnabled))
      .map((fieldName) => {
        const rateItem: any = itemsWithRates.find(
          (i: any) => i.fieldName === fieldName && i.category === category,
        );
        return String(rateItem?.resourceCode ?? rateLookupMap.get(fieldName)?.resourceCode ?? '').trim();
      })
      .filter(Boolean)
      .join('+');

    result.set(category, {
      resourceCode,
      quantityField: config.quantityField,
      totalPlatformQty: platformQty,
      billSummaryAmount,
      // 2dp-rounded `combinedRate` — the visible per-unit rate. Options B and C
      // do literal `rowPlatformQty × combinedRate` math on this value so the
      // displayed cell matches `qty × visibleRate` exactly.
      combinedRate,
    });
  });

  return result;
}

/**
 * Plot Matrix Scaffold column name → BoQ `SCAFFOLD_CATEGORIES` key. The
 * Plot Matrix labels are different to keep the on-screen heading style
 * consistent with other valuation stages, but each Plot Matrix column maps
 * 1:1 to a BoQ Scaffold category by the sub-field list.
 */
const PLOT_MATRIX_SCAFFOLD_COLUMN_TO_BOQ_CATEGORY: Record<string, string> = {
  'Scaffold Platforms - Scaffold Platforms': 'Scaffold Platforms',
  'Scaffold Platforms - Loading Bays': 'Loading Bays',
  'Scaffold Platforms - Removal': 'Stair Access',
};

/**
 * Per-row Scaffold cell value using the proportional Bill Summary share of
 * the consolidated BoQ amount. `categoryName` is the Plot Matrix column
 * name; it gets translated to a BoQ Scaffold category via
 * `PLOT_MATRIX_SCAFFOLD_COLUMN_TO_BOQ_CATEGORY`, then we distribute the
 * pre-computed billSummaryAmount by `(rowPlatformQty / totalPlatformQty)`.
 */
function computeScaffoldCellAmount(
  rowData: any,
  categoryName: string,
  scaffoldCategoryData: Map<string, ScaffoldCategoryData>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  const boqCategory = PLOT_MATRIX_SCAFFOLD_COLUMN_TO_BOQ_CATEGORY[categoryName];
  if (!boqCategory) return 0;
  const data = scaffoldCategoryData.get(boqCategory);
  if (!data) return 0;
  const rowPlatformQty = Number(rowData?.[data.quantityField]) || 0;
  if (rowPlatformQty === 0) return 0;
  if (strategy === 'rawRowQty') {
    return rowPlatformQty * data.combinedRate;
  }
  if (strategy === 'roundedRowQty') {
    return Math.round(rowPlatformQty) * data.combinedRate;
  }
  if (data.totalPlatformQty === 0) return 0;
  return (rowPlatformQty / data.totalPlatformQty) * data.billSummaryAmount;
}

/**
 * Garden: cached per-field {totalQty, billSummaryAmount} computed via the same
 * pipeline the Garden Bill Summary uses (`generateBOQSummaryWithCategories`
 * with `GARDEN_CATEGORIES` and `gardenDimensionValues` →
 * `createGardenRateLookupMap` → `applyGardenRatesToSummary` → the Bill
 * Summary's per-row rounding rule `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))`).
 * `applyGardenRatesToSummary` already applies the m³→m² thickness multiplier
 * via `getM3ToM2Multiplier` for topsoil / subsoil / no-dig layer fields, so
 * `item.rate` is the effective m² rate the Bill Summary displays.
 *
 * Same proportional-share contract as Foundations: distributing `rowQty /
 * totalQty × billSummaryAmount` across all garden rows sums to the Bill
 * Summary amount for that field.
 */
function computeGardenFieldBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }> {
  const result = new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  if (!itemLibraryData || !groundworksData?.gardenTable?.length) return result;

  const gardenDimensionValues = (groundworksData as any)?.gardenDimensionValues as
    | Record<string, string>
    | undefined;

  // Match the on-screen `GardenBillSummary` exactly — it omits `columnHeaders`
  // and relies entirely on `applyGardenRatesToSummary` for rate resolution.
  // Passing headers here diverges from the user-visible BoQ total.
  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Garden',
    GARDEN_CATEGORIES,
    gardenDimensionValues,
    null,
  );
  const rateLookupMap = createGardenRateLookupMap(itemLibraryData, groundworksData);
  const items = applyGardenRatesToSummary(baseItems, rateLookupMap);

  // No occurrence-based scaling — each Plot Matrix Garden cell shows the
  // literal `rowQty × rate` for its field. If a field is ever re-listed in
  // multiple Plot Matrix Garden columns (the current `PLOT_MATRIX_GARDEN_
  // CATEGORIES` partitions cleanly after the no-dig-layer / geotextile fields
  // were dropped from "Place Clean Cap"), it would contribute its full amount
  // to every column it's listed in, so the Plot Matrix row total could
  // exceed the BoQ Garden total. BoQ totals are independent — `GARDEN_
  // CATEGORIES` partitions cleanly, so the Garden Bill Summary stays correct.
  items.forEach((item: any) => {
    const rawQty = item.quantity || 0;
    const roundedQty = Math.round(rawQty);
    const rate = parseFloat(((item.rate || 0) as number).toFixed(2));
    const fullAmount = parseFloat(Math.abs(roundedQty * rate).toFixed(2));
    result.set(item.fieldName, {
      totalQty: rawQty,
      billSummaryAmount: fullAmount,
      rate,
      resourceCode: rateLookupMap.get(item.fieldName)?.resourceCode ?? '',
    });
  });

  return result;
}

/**
 * Per-row Garden cell value. Strategy controls the decomposition — see
 * `CellCalcStrategy`. `PLOT_MATRIX_GARDEN_CATEGORIES` currently partitions
 * fields cleanly (the no-dig-layer / geotextile fields used to appear under
 * both "Import Clean Cap" and "Place Clean Cap" but were removed from the
 * latter per user request). If a field is ever re-duplicated across columns,
 * each cell still computes the literal `rowQty × rate` for its column's
 * fieldNames — by design, since users want each cell to read as the field's
 * `rowQty × rate`. Plot Matrix row totals could therefore exceed the BoQ
 * Garden total in that scenario; the BoQ pipeline uses `GARDEN_CATEGORIES`
 * (no duplicates) so it stays correct.
 */
function computeGardenCellAmount(
  rowData: any,
  fieldNames: string[],
  gardenFieldData: Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  let total = 0;
  fieldNames.forEach((fieldName) => {
    const data = gardenFieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    if (strategy === 'rawRowQty') {
      total += rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      total += Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      total += (rowQty / data.totalQty) * data.billSummaryAmount;
    }
  });
  return total;
}

/**
 * Plot Drives: cached per-field {totalQty, billSummaryAmount} computed via the
 * same pipeline the Plot Drives Bill Summary uses
 * (`generateBOQSummaryWithCategories` with `PLOT_DRIVES_CATEGORIES` +
 * `plotDrivesDimensionValues` + reconstructed columnHeaders →
 * `createPlotDrivesRateLookupMap` → `applyPlotDrivesRatesToSummary` → the Bill
 * Summary's per-row rounding rule
 * `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))`).
 *
 * `applyPlotDrivesRatesToSummary` already bakes the m³→m² thickness multiplier
 * into `item.rate`, so the share math operates on the m²-equivalent amount the
 * Bill Summary actually displays — that's where the decimal drift was coming
 * from on the previous `quantity × rate` per-row path.
 *
 * Same proportional-share contract as Foundations / Garden / Cut & Fill so
 * `Σ_rows cellContribution = billSummaryAmount` for every Plot Drives field.
 * Field names match the **transformed** semantic format (the BoQ generator
 * runs `transformPlotDrivesData` internally before summing quantities, and the
 * pass-1 caller passes already-transformed `stepPlotData` here too).
 */
function computePlotDrivesFieldBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
  ratesMapping?: RateMappingJSON | null,
): Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }> {
  const result = new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  if (!itemLibraryData || !groundworksData?.plotDrivesTable?.length) return result;

  const dimensionValues = (groundworksData as any)?.plotDrivesDimensionValues;
  const columnHeaders = reconstructPlotDrivesColumnHeaders(
    (groundworksData as any)?.plotDrivesColumnHeaders || {},
    (groundworksData as any)?.plotDrivesDimensionValues || {},
  );

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Plot Drives',
    PLOT_DRIVES_CATEGORIES,
    dimensionValues,
    ratesMapping,
    columnHeaders,
  );
  const rateLookupMap = createPlotDrivesRateLookupMap(itemLibraryData, groundworksData);
  const items = applyPlotDrivesRatesToSummary(baseItems, rateLookupMap);

  items.forEach((item: any) => {
    const rawQty = item.quantity || 0;
    const roundedQty = Math.round(rawQty);
    const rate = parseFloat(((item.rate || 0) as number).toFixed(2));
    result.set(item.fieldName, {
      totalQty: rawQty,
      billSummaryAmount: parseFloat(Math.abs(roundedQty * rate).toFixed(2)),
      rate,
      resourceCode: rateLookupMap.get(item.fieldName)?.resourceCode ?? '',
    });
  });

  return result;
}

/**
 * Per-row Plot Drives cell value. Strategy controls the decomposition — see
 * `CellCalcStrategy`. `rowData` must already be in transformed (semantic) format.
 * `rate` is 2dp-rounded (the visible rate).
 */
function computePlotDrivesCellAmount(
  rowData: any,
  fieldNames: string[],
  plotDrivesFieldData: Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  let total = 0;
  fieldNames.forEach((fieldName) => {
    const data = plotDrivesFieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    if (strategy === 'rawRowQty') {
      total += rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      total += Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      total += (rowQty / data.totalQty) * data.billSummaryAmount;
    }
  });
  return total;
}

/**
 * Plot Pavings: cached per-field {totalQty, billSummaryAmount} computed via the
 * same pipeline the Plot Pavings Bill Summary uses
 * (`generateBOQSummaryWithCategories` with `PLOT_PAVINGS_CATEGORIES` +
 * `plotPavingsDimensionValues` + reconstructed columnHeaders →
 * `createPlotPavingsRateLookupMap` → `applyPlotPavingsRatesToSummary` → the
 * Bill Summary's per-row rounding rule
 * `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))`).
 *
 * `applyPlotPavingsRatesToSummary` already bakes the m³→m² thickness multiplier
 * into `item.rate`, so the share math operates on the m²-equivalent amount the
 * Bill Summary actually displays — same fix as Plot Drives.
 *
 * Same proportional-share contract as Foundations / Garden / Plot Drives so
 * `Σ_rows cellContribution = billSummaryAmount` for every Plot Pavings field.
 * Plot Pavings field names in `plotPavingsTable` are already in semantic
 * format (no equivalent of `transformPlotDrivesData` is needed).
 */
function computePlotPavingsFieldBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
  ratesMapping?: RateMappingJSON | null,
): Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }> {
  const result = new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  if (!itemLibraryData || !groundworksData?.plotPavingsTable?.length) return result;

  const dimensionValues = (groundworksData as any)?.plotPavingsDimensionValues;
  const columnHeaders = reconstructPlotPavingsColumnHeaders(
    (groundworksData as any)?.plotPavingsColumnHeaders || {},
    (groundworksData as any)?.plotPavingsDimensionValues || {},
  );

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Plot Pavings',
    PLOT_PAVINGS_CATEGORIES,
    dimensionValues,
    ratesMapping,
    columnHeaders,
  );
  const rateLookupMap = createPlotPavingsRateLookupMap(itemLibraryData, groundworksData);
  const items = applyPlotPavingsRatesToSummary(baseItems, rateLookupMap);

  items.forEach((item: any) => {
    const rawQty = item.quantity || 0;
    const roundedQty = Math.round(rawQty);
    const rate = parseFloat(((item.rate || 0) as number).toFixed(2));
    result.set(item.fieldName, {
      totalQty: rawQty,
      billSummaryAmount: parseFloat(Math.abs(roundedQty * rate).toFixed(2)),
      rate,
      resourceCode: rateLookupMap.get(item.fieldName)?.resourceCode ?? '',
    });
  });

  return result;
}

/**
 * Per-row Plot Pavings cell value. Strategy controls the decomposition — see
 * `CellCalcStrategy`. `rate` is 2dp-rounded (the visible rate).
 */
function computePlotPavingsCellAmount(
  rowData: any,
  fieldNames: string[],
  plotPavingsFieldData: Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  let total = 0;
  fieldNames.forEach((fieldName) => {
    const data = plotPavingsFieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    if (strategy === 'rawRowQty') {
      total += rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      total += Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      total += (rowQty / data.totalQty) * data.billSummaryAmount;
    }
  });
  return total;
}

/**
 * Cut & Fills: cached per-field {totalQty, billSummaryAmount} computed via the
 * same pipeline the Cut & Fill Bill Summary uses (`generateBOQSummaryWithCategories`
 * with `CUT_FILL_CATEGORIES` → `createCutFillsRateLookupMap` →
 * `applyCutFillsRatesToSummary` → the Bill Summary's per-row rounding rule
 * `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))`). Same proportional
 * share contract as Foundations / Garden so per-plot cells reflect that plot's
 * own quantities × rate while the column total still matches the Bill Summary
 * grand total exactly.
 *
 * Note: the soil-management section (Section 3.1 Topsoil / 3.2 Subsoil) is
 * NOT here — those are derived from `Math.abs(sum(onsite) - sum(offsite)) × rate`
 * at the aggregate level, so they remain on the existing
 * `soilBoQTotals.X / numberOfPlots` distribution path (per-plot abs() can't be
 * summed to match the aggregate abs()).
 */
export function computeCutFillFieldBillSummaryData(
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }> {
  const result = new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
  if (!itemLibraryData || !groundworksData?.cutFillsTable?.length) return result;

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData as any,
    'Cut & Fills',
    CUT_FILL_CATEGORIES,
    undefined,
    null,
  );
  const rateLookupMap = createCutFillsRateLookupMap(itemLibraryData);
  const items = applyCutFillsRatesToSummary(baseItems, rateLookupMap);

  items.forEach((item: any) => {
    const rawQty = item.quantity || 0;
    const roundedQty = Math.round(rawQty);
    const rate = parseFloat(((item.rate || 0) as number).toFixed(2));
    result.set(item.fieldName, {
      totalQty: rawQty,
      billSummaryAmount: parseFloat(Math.abs(roundedQty * rate).toFixed(2)),
      rate,
      resourceCode: rateLookupMap.get(item.fieldName)?.resourceCode ?? '',
    });
  });

  return result;
}

/**
 * Per-row Cut & Fill cell value. Strategy controls the decomposition — see
 * `CellCalcStrategy`. `rate` is 2dp-rounded (the visible rate).
 */
function computeCutFillCellAmount(
  rowData: any,
  fieldNames: string[],
  cutFillFieldData: Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>,
  strategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
): number {
  let total = 0;
  fieldNames.forEach((fieldName) => {
    const data = cutFillFieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    if (strategy === 'rawRowQty') {
      total += rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      total += Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      total += (rowQty / data.totalQty) * data.billSummaryAmount;
    }
  });
  return total;
}

/**
 * Cut & Fill plot-matrix categories that source their amount from the Cut &
 * Fill Bill Summary (Sections 1.0 Site Clearance + 2.0 Cut & Fill). The three
 * soil-management categories — "Site Strip - Topsoil Disposal", "Cut/Fill -
 * Disposal Of Surplus", "Cut/Fill - Import of Deficit" — are NOT included here;
 * they're already special-cased via `soilBoQTotals` further down.
 */
// Reverted to pre-D2 set so the four Cut/Fill columns rejoin the proportional
// Bill Summary share path (`computeCutFillCellAmount`). The deviation-report
// Either/Or per-plot version broke BoQ total parity by ~rounding-residual.
const CUT_FILL_NON_SOIL_PLOT_MATRIX_CATEGORIES: ReadonlySet<string> = new Set([
  ...Object.keys(PLOT_MATRIX_SITE_STRIP_CATEGORIES).filter(
    (name) => name !== 'Site Strip - Topsoil Disposal',
  ),
  ...Object.keys(PLOT_MATRIX_CUT_FILL_CATEGORIES).filter(
    (name) => name !== 'Cut/Fill - Disposal Of Surplus' && name !== 'Cut/Fill - Import of Deficit',
  ),
]);


// ─── Dev-mode per-cell breakdown helpers ────────────────────────────────────

type StandardFieldData = {
  totalQty: number; billSummaryAmount: number; rate: number; resourceCode?: string;
};

function buildStandardCellBreakdown(
  rowData: any,
  fieldNames: string[],
  fieldData: Map<string, StandardFieldData>,
  strategy: CellCalcStrategy,
): CellBreakdown {
  const fields: CellBreakdownField[] = [];
  let preUpliftTotal = 0;
  fieldNames.forEach((fieldName) => {
    const data = fieldData.get(fieldName);
    if (!data) return;
    const rowQty = Number(rowData?.[fieldName]) || 0;
    if (rowQty === 0) return;
    let contribution = 0;
    if (strategy === 'rawRowQty') {
      contribution = rowQty * data.rate;
    } else if (strategy === 'roundedRowQty') {
      contribution = Math.round(rowQty) * data.rate;
    } else {
      if (data.totalQty === 0) return;
      contribution = (rowQty / data.totalQty) * data.billSummaryAmount;
    }
    preUpliftTotal += contribution;
    fields.push({ fieldName, resourceCode: data.resourceCode ?? '', rowQty, totalQty: data.totalQty, billSummaryAmount: data.billSummaryAmount, modifiedRate: data.rate, contribution });
  });
  return { fields, strategy, preUpliftTotal };
}

function buildScaffoldCellBreakdown(
  rowData: any,
  categoryName: string,
  scaffoldCategoryData: Map<string, ScaffoldCategoryData>,
  strategy: CellCalcStrategy,
): CellBreakdown | undefined {
  const boqCategory = PLOT_MATRIX_SCAFFOLD_COLUMN_TO_BOQ_CATEGORY[categoryName];
  if (!boqCategory) return undefined;
  const data = scaffoldCategoryData.get(boqCategory);
  if (!data) return undefined;
  const rowQty = Number(rowData?.[data.quantityField]) || 0;
  if (rowQty === 0) return undefined;
  let contribution = 0;
  if (strategy === 'rawRowQty') {
    contribution = rowQty * data.combinedRate;
  } else if (strategy === 'roundedRowQty') {
    contribution = Math.round(rowQty) * data.combinedRate;
  } else {
    if (data.totalPlatformQty === 0) return undefined;
    contribution = (rowQty / data.totalPlatformQty) * data.billSummaryAmount;
  }
  return {
    fields: [{ fieldName: data.quantityField, resourceCode: data.resourceCode, rowQty, totalQty: data.totalPlatformQty, billSummaryAmount: data.billSummaryAmount, modifiedRate: data.combinedRate, contribution }],
    strategy,
    preUpliftTotal: contribution,
  };
}

function buildDivisionCellBreakdown(billSummaryTotal: number, numberOfPlots: number): CellBreakdown {
  const contribution = numberOfPlots > 0 ? billSummaryTotal / numberOfPlots : 0;
  return {
    fields: [{ fieldName: '(all plots equally)', resourceCode: '', rowQty: 1, totalQty: numberOfPlots, billSummaryAmount: billSummaryTotal, modifiedRate: contribution, contribution }],
    strategy: 'proportional',
    preUpliftTotal: contribution,
  };
}

/**
 * Build a detailed per-sub-item CellBreakdown for Drainage cells.
 *
 * Drainage cells use Rule C: `cell = billSummaryTotal / numberOfPlots`.
 * The bill summary total is composed of:
 *   • A per-plot row (combined rate across all bend/pipe/gravel fields)
 *   • NME PPIC item
 *   • 600mm NME PPIC item
 *   • (Storm only) Yard Gully, Drainage Channel, Land Drain items
 *
 * Each sub-item gets one breakdown field showing:
 *   rowQty=1, totalQty=numberOfPlots, billSummaryAmount=sub-item total,
 *   modifiedRate=billSummaryAmount/numberOfPlots, contribution=modifiedRate.
 * So `Σ contribution = billSummaryTotal / numberOfPlots = cell`.
 */
function buildDrainageCellBreakdown(
  groundworksData: GroundworksStepData,
  categoryName: string,
  numberOfPlots: number,
  billSummaryTotal: number,
  ratesMapping: RateMappingJSON | null | undefined,
  itemLibraryData: ItemLibraryResponse | undefined,
): CellBreakdown {
  void ratesMapping;
  const drainageRows: any[] = (groundworksData as any)?.drainageTableStandard || (groundworksData as any)?.drainageTable || [];
  const osmaConfig = (groundworksData as any)?.drainageDimensionValues || {};
  const polypipeConfig = (groundworksData as any)?.drainagePolypipeConfig || {};
  const rateLookupMap = itemLibraryData
    ? createDrainageRateLookupMap(itemLibraryData, groundworksData)
    : new Map<string, { rate: number; resourceCode?: string }>();

  const sumField = (field: string): number =>
    drainageRows.reduce((acc: number, row: any) => acc + (parseFloat(row[field]) || 0), 0);

  const getRate = (fieldName: string): number =>
    (rateLookupMap.get(fieldName) as { rate: number } | undefined)?.rate || 0;

  const getCode = (fieldName: string): string =>
    (rateLookupMap.get(fieldName) as { resourceCode?: string } | undefined)?.resourceCode || '';

  const calcPerPlotAmount = (
    combinedFields: readonly string[],
    totalPlots: number,
    nrPlotsTakeOff: number,
  ): number => {
    const combinedSum = combinedFields.reduce((sum, f) => sum + sumField(f) * getRate(f), 0);
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    return parseFloat((Math.round(totalPlots) * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const calcItemAmount = (fieldName: string, qtyOverride?: number): number => {
    const quantity = qtyOverride !== undefined ? qtyOverride : sumField(fieldName);
    const rate = getRate(fieldName);
    return parseFloat((Math.round(quantity) * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const hasOsma = drainageRows.some(
    (row: any) => (parseFloat(row.osma32mmBend) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend) || 0) > 0,
  );
  const hasPolypipe = drainageRows.some(
    (row: any) => (parseFloat(row.polypipe32mmBend) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend) || 0) > 0,
  );
  const usePolypipe = hasPolypipe && !hasOsma;
  const isFoul = categoryName === 'Plot Drainage - Foul';

  const fields: CellBreakdownField[] = [];
  let preUpliftTotal = 0;

  const addField = (label: string, resourceCode: string, subTotal: number) => {
    if (subTotal === 0) return;
    const contribution = numberOfPlots > 0 ? subTotal / numberOfPlots : 0;
    fields.push({
      fieldName: label,
      resourceCode,
      rowQty: 1,
      totalQty: numberOfPlots,
      billSummaryAmount: subTotal,
      modifiedRate: contribution,
      contribution,
    });
    preUpliftTotal += contribution;
  };

  if (isFoul) {
    if (usePolypipe) {
      const ppTotal = parseFloat(polypipeConfig.totalPlots) || 0;
      const ppNr = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
      addField('Per-plot row (Polypipe Foul)', 'GWALPDD001A', calcPerPlotAmount(POLYPIPE_FOUL_COMBINED_FIELDS, ppTotal, ppNr));
      addField('NME PPIC (Polypipe Foul)', 'GWALPDD018+GWALPDD019', calcItemAmount('polypipeNmePpic'));
      addField('600mm NME PPIC (Polypipe Foul)', getCode('polypipe600mmNmePpic'), calcItemAmount('polypipe600mmNmePpic'));
    } else {
      const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
      const osmaNr = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
      addField('Per-plot row (OSMA Foul)', 'GWALPDD001A', calcPerPlotAmount(OSMA_FOUL_COMBINED_FIELDS, osmaTotalPlots, osmaNr));
      addField('NME PPIC (OSMA Foul)', 'GWALPDD018+GWALPDD019', calcItemAmount('osmaNmePpic'));
      addField('600mm NME PPIC (OSMA Foul)', getCode('osma600mmNmePpic'), calcItemAmount('osma600mmNmePpic'));
    }
  } else {
    // Storm
    if (usePolypipe) {
      const ppTotal = parseFloat(polypipeConfig.totalPlots) || 0;
      const ppNr = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
      addField('Per-plot row (Polypipe Storm)', 'GWALPDD001A', calcPerPlotAmount(POLYPIPE_STORM_COMBINED_FIELDS, ppTotal, ppNr));
      addField('NME PPIC (Polypipe Storm)', 'GWALPDD018+GWALPDD019', calcItemAmount('polypipeStormNmePpic'));
      addField('600mm NME PPIC (Polypipe Storm)', getCode('polypipeStorm600mmNmePpic'), calcItemAmount('polypipeStorm600mmNmePpic'));
      addField('Yard Gully', getCode('polypipeStormYardGully'), calcItemAmount('polypipeStormYardGully', sumField('polypipeStormYardGully') || sumField('yardGully')));
      addField('Drainage Channel', getCode('polypipeStormDrainageChannel'), calcItemAmount('polypipeStormDrainageChannel', sumField('polypipeStormDrainageChannel') || sumField('drainageChannel')));
      addField('Land Drain', getCode('polypipeStormLandDrain'), calcItemAmount('polypipeStormLandDrain', sumField('polypipeStormLandDrain') || sumField('landDrain')));
    } else {
      const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
      const osmaNr = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
      addField('Per-plot row (OSMA Storm)', 'GWALPDD001A', calcPerPlotAmount(OSMA_STORM_COMBINED_FIELDS, osmaTotalPlots, osmaNr));
      addField('NME PPIC (OSMA Storm)', 'GWALPDD018+GWALPDD019', calcItemAmount('osmaStormNmePpic'));
      addField('600mm NME PPIC (OSMA Storm)', getCode('osmaStorm600mmNmePpic'), calcItemAmount('osmaStorm600mmNmePpic'));
      addField('Yard Gully', getCode('osmaStormYardGully'), calcItemAmount('osmaStormYardGully', sumField('osmaStormYardGully') || sumField('yardGully')));
      addField('Drainage Channel', getCode('osmaStormDrainageChannel'), calcItemAmount('osmaStormDrainageChannel', sumField('osmaStormDrainageChannel') || sumField('drainageChannel')));
      addField('Land Drain', getCode('osmaStormLandDrain'), calcItemAmount('osmaStormLandDrain', sumField('osmaStormLandDrain') || sumField('landDrain')));
    }
  }

  if (fields.length === 0) {
    return buildDivisionCellBreakdown(billSummaryTotal, numberOfPlots);
  }

  return { fields, strategy: 'proportional', preUpliftTotal };
}

function buildScreenWallsFoundationBreakdown(
  retRow: any,
  retainingWallsTable: any[],
  itemLibraryData: ItemLibraryResponse | undefined,
  screenWallsLabel?: string,
  eoPillarLabel?: string,
): CellBreakdown | undefined {
  const evaluateRateMap = buildEvaluateRateMap(itemLibraryData);
  const fieldDefs: Array<{ fieldName: 'screenWalls' | 'screenWallsEO' | 'screenWallsAdditionalExcavation' | 'screenWallsAdditionalConcrete'; resourceCode: string }> = [
    { fieldName: 'screenWalls',   resourceCode: resolveScreenWallsCode(screenWallsLabel) },
    { fieldName: 'screenWallsEO', resourceCode: resolveEOPillarCode(eoPillarLabel) },
    { fieldName: 'screenWallsAdditionalExcavation', resourceCode: 'GWALPGW0026' },
    { fieldName: 'screenWallsAdditionalConcrete',   resourceCode: 'GWALPGW0015' },
  ];
  const fields: CellBreakdownField[] = [];
  let preUpliftTotal = 0;
  fieldDefs.forEach(({ fieldName, resourceCode }) => {
    const rowQty = Number(retRow?.[fieldName]) || 0;
    if (rowQty === 0) return;
    const rate = evaluateRateMap.get(resourceCode) || 0;
    const totalQty = retainingWallsTable.reduce((s, r) => s + (Number(r[fieldName]) || 0), 0);
    const billSummaryAmount = totalQty * rate;
    const contribution = rowQty * rate;
    preUpliftTotal += contribution;
    fields.push({ fieldName, resourceCode, rowQty, totalQty, billSummaryAmount, modifiedRate: rate, contribution });
  });
  if (fields.length === 0) return undefined;
  return { fields, strategy: 'rawRowQty', preUpliftTotal };
}

function buildRetainingWallsFoundationBreakdown(
  retRow: any,
  groundworksData: GroundworksStepData,
  itemLibraryData: ItemLibraryResponse | undefined,
): CellBreakdown | undefined {
  const evaluateRateMap = buildEvaluateRateMap(itemLibraryData);
  const retainingWallsTable: any[] = groundworksData.retainingWallsTable || [];
  const facingBrickworkLabel = (groundworksData as any)?.wallsBuildUpConfig?.facingBrickworkLabel;
  const fields: CellBreakdownField[] = [];
  let preUpliftTotal = 0;

  // ── Part 1: direct-rate screen wall item fields ──────────────────────────
  // (screenWallsAdditionalExcavation/Concrete moved to
  // buildScreenWallsFoundationBreakdown, under "Screen Walls - upto DPC".)
  const part1Defs: Array<{ fieldName: string; resourceCode: string }> = [
    { fieldName: 'screenWallsSof',         resourceCode: 'GWALPGW0043' },
    { fieldName: 'screenWallsLog',         resourceCode: 'ACL012A'     },
    { fieldName: 'screenWallsGravelBoard', resourceCode: 'GWALPGW0023'  },
  ];
  part1Defs.forEach(({ fieldName, resourceCode }) => {
    const rowQty = Number(retRow?.[fieldName]) || 0;
    if (rowQty === 0) return;
    const rate = evaluateRateMap.get(resourceCode) || 0;
    const totalQty = retainingWallsTable.reduce((s, r) => s + (Number(r[fieldName]) || 0), 0);
    const billSummaryAmount = totalQty * rate;
    const contribution = rowQty * rate;
    preUpliftTotal += contribution;
    fields.push({ fieldName, resourceCode, rowQty, totalQty, billSummaryAmount, modifiedRate: rate, contribution });
  });

  // ── Part 2: per-item build-up rows for every wall spec ───────────────────
  // Sourced from the same `computeRetainingWallSpecAmounts` /
  // `computeGeoToberWallSpecAmounts` calculation the actual cell total (in
  // `calculateCategoryTotalForRow`) and the Bill Summaries use, so this
  // dev-mode breakdown can't show a different rate resolution than what's
  // actually priced.
  const wallsBuildUpData   = (groundworksData as any).wallsBuildUpData             as Record<string, any[]>          || {};
  const wallsBuildUpDims   = (groundworksData as any).wallsBuildUpDimensions        as Record<string, WallBuildUpDimensionItem[]> | undefined;
  const geoToberBuildUpData = (groundworksData as any).geoToberWallBuildUpData      as Record<string, any[]>          || {};
  const geoToberBuildUpDims = (groundworksData as any).geoToberWallBuildUpDimensions as Record<string, WallBuildUpDimensionItem[]> | undefined;

  const retSpecAmounts = computeRetainingWallSpecAmounts(
    retainingWallsTable, wallsBuildUpData, wallsBuildUpDims, evaluateRateMap, facingBrickworkLabel, 1,
  );
  const geoSpecAmounts = computeGeoToberWallSpecAmounts(
    groundworksData.geoToberWallTable, geoToberBuildUpData, geoToberBuildUpDims, evaluateRateMap, 1,
  );

  const plotKey = normalizePlotNoKey(retRow.plotNo);
  const geoToberPlotRow = ((groundworksData.geoToberWallTable || []) as any[]).find(
    (r: any) => normalizePlotNoKey(r.plotNo) === plotKey,
  ) || {};

  const wallSources: Array<{
    fieldMap: Record<string, string>;
    plotRow: any;
    specAmounts: Map<string, { wallQtyM: number; items: Array<{ description: string; net: number; rate: number; evaluateCode: string }> }>;
  }> = [
    { fieldMap: wallFieldToSpecId, plotRow: retRow, specAmounts: retSpecAmounts },
    { fieldMap: geoToberWallFieldToSpecId, plotRow: geoToberPlotRow, specAmounts: geoSpecAmounts },
  ];

  wallSources.forEach(({ fieldMap, plotRow, specAmounts }) => {
    Object.entries(fieldMap).forEach(([fieldName, specId]) => {
      const rowMetres = Number(plotRow[fieldName]) || 0;
      if (rowMetres === 0) return;
      const amt = specAmounts.get(specId);
      if (!amt || amt.wallQtyM === 0) return;
      const totalMetres = amt.wallQtyM;

      amt.items.forEach((item) => {
        const absNet = Math.abs(item.net || 0);
        if (absNet === 0 || item.rate === 0) return;

        const contribution = rowMetres * absNet * item.rate;
        const billSummaryAmount = totalMetres * absNet * item.rate;
        preUpliftTotal += contribution;
        fields.push({
          fieldName: `${specId} — ${item.description}`,
          resourceCode: item.evaluateCode || '',
          rowQty: rowMetres,
          totalQty: totalMetres,
          billSummaryAmount,
          modifiedRate: item.rate,
          contribution,
          net: absNet,
        });
      });
    });
  });

  if (fields.length === 0) return undefined;
  return { fields, strategy: 'proportional', preUpliftTotal };
}

export const generatePlotMatrixCategoryRows = (
  groundworksData: GroundworksStepData,
  columns: PlotMatrixCategoryColumn[],
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  soilBoQTotals?: SoilManagementBoQTotals,
  ohpPercent?: number,
  // Per-cell decomposition strategy. Defaults to `proportional` (current
  // behavior) so existing call sites that don't pass a strategy are unchanged.
  // See `CellCalcStrategy` for the three options. The dev-mode UI in
  // PlotMatrix.tsx is the intended writer of this parameter.
  cellCalcStrategy: CellCalcStrategy = DEFAULT_CELL_CALC_STRATEGY,
  // Prelims + OH&P uplift factor applied to every cell `total` at the end of
  // row generation, so EVERY column (not just Site Strip / Cut/Fill) carries
  // the same `(1 + prelim% + ohp%)` multiplier. Default `1` keeps legacy
  // callers (Excel export, dev-mode reconciliation) producing base BoQ values
  // when they pass no factor. Mathematically identical to the previous
  // `applyPlotMatrixUplift(rows, upliftFactor)` post-processing step, just
  // baked in here so cells are always uplifted at source.
  upliftFactor: number = 1,
  // Optional canonical BoQ grand total target (already uplifted) for the
  // whole-matrix reconciliation pass inside `applyPlotMatrixUplift`. Kept for
  // backward compatibility — prefer `boqStepTargets` for per-section delta
  // distribution (see below).  When both are omitted, only per-column Hamilton
  // runs.
  boqCanonicalGrandTotal?: number,
  // Per-section BoQ targets (already uplifted) used for the preferred
  // per-section reconciliation mode.  When provided alongside the `columns`
  // array (which carries `stepName`), any delta between the Plot Matrix section
  // total and the BoQ section total is distributed only within that section's
  // columns — not spread across the whole matrix.
  boqStepTargets?: BoqStepTargets,
): PlotMatrixRow[] => {
  const rows: PlotMatrixRow[] = [];

  // ─── Build plot identity union ───────────────────────────────────────────
  // Plot rows can be present in any of cutFillsTable / foundationsTable /
  // scaffoldTable — iterating just cutFillsTable used to drop foundation-only
  // tenders entirely. Walk all three (cutFillsTable first for ordering), skip
  // totals/garage rows, and dedup via the normalised plotNo key (`P-1` ≡ `1`).
  type PlotIdentity = { plotNo: string; housetype: string };
  const plotIdentities: PlotIdentity[] = [];
  const seenPlotKeys = new Set<string>();
  const pushPlotIdentity = (plotNo: any, housetype: any, plotType: any) => {
    const raw = String(plotNo ?? '').trim();
    if (!raw) return;
    if (raw === 'TOTALS' || raw === 'Total' || raw === 'totals') return;
    // Defensive backstop: drainage and other takeoff Excel imports can carry
    // free-text label rows in the plotNo column (e.g. "Specified Supplier",
    // "Manhole Schedule"). Plot/garage identifiers are compact tokens — they
    // never contain whitespace and are short. The old digit-only guard
    // incorrectly dropped legitimate alphabetic or special-character IDs
    // (e.g. "A", "B", "1A", "#2", "A/1"). We now reject multi-word strings
    // (contain whitespace) and anything suspiciously long (> 20 chars).
    if (/\s/.test(raw) || raw.length > 20) return;
    // Only collect plot rows (or legacy rows with no explicit plotType).
    if (plotType === 'garage') return;
    const key = normalizePlotNoKey(raw);
    if (!key || seenPlotKeys.has(key)) return;
    seenPlotKeys.add(key);
    plotIdentities.push({ plotNo: raw, housetype: String(housetype ?? '') });
  };
  // Walk every data table that can carry plot rows. Previously only cutFills /
  // foundations / scaffold were included, so any plot that existed solely in
  // gardenTable / plotDrivesTable / plotPavingsTable / plotServiceTable /
  // drainageTable / retainingWallsTable was dropped from Plot Matrix entirely.
  // That made the per-plot proportional share sum to less than the BoQ amount
  // — the more "garden-only" (or similar) plots the take-off had, the larger
  // the gap. Iterating every relevant table closes that gap.
  (groundworksData.cutFillsTable || []).forEach((r: any) => {
    if (r?.key === 'totals') return;
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.foundationsTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.scaffoldTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.gardenTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.plotDrivesTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.plotPavingsTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.plotServiceTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  ((groundworksData.drainageTableStandard || groundworksData.drainageTable) || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.retainingWallsTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });
  (groundworksData.geoToberWallTable || []).forEach((r: any) => {
    pushPlotIdentity(r?.plotNo, r?.housetype, r?.plotType);
  });

  if (plotIdentities.length === 0 && !groundworksData?.foundationsTable?.length && !groundworksData?.scaffoldTable?.length) {
    return [];
  }

  const numberOfPlots = plotIdentities.length;

  // Per-field Cut & Fill Bill Summary data — used to distribute each plot's
  // Cut & Fill cells proportionally to that plot's qty share. Each cell shows
  // the plot's own qty × rate (effectively), NOT grand total / numberOfPlots
  // (which made every plot look identical). Soil-management categories still
  // distribute via `soilBoQTotals` because aggregate `abs(sum onsite − sum
  // offsite)` can't be reconstructed by summing per-plot abs().
  const cutFillFieldData = computeCutFillFieldBillSummaryData(groundworksData, itemLibraryData);

  // Per-field Foundation Bill Summary data (totalQty + billSummaryAmount) used
  // to distribute each foundation row's cell proportionally to its qty share,
  // so the matrix Foundation column totals match the Foundations Bill Summary
  // grand total exactly while preserving per-plot / per-garage variation.
  const foundationsFieldData = computeFoundationsFieldBillSummaryData(groundworksData, itemLibraryData);

  // Per-field Garden Bill Summary data — same proportional share contract as
  // Foundations, so the matrix Garden column totals match the Garden Bill
  // Summary grand total exactly. `applyGardenRatesToSummary` already applies
  // the m³→m² thickness multiplier in the rate, so the share math works on
  // the m²-equivalent amount the Bill Summary displays.
  const gardenFieldData = computeGardenFieldBillSummaryData(groundworksData, itemLibraryData);

  // Per-field Plot Drives Bill Summary data — same proportional share contract.
  // Previously this step fell through to the generic `qty × rate` per-row
  // formula, which drifted from the Bill Summary's
  // `Math.abs(Math.round(qty) × parseFloat(rate.toFixed(2)))` per-field rule —
  // especially noticeable when adding a row with decimal-thickness fields
  // (HRA, DBM, Type 1 sub-base) where the m³→m² rate carries decimals.
  // Used for all three Plot Drives step names (`Plot Drives`, `Private Drives
  // Surface`, `Shared Drives Surface`) — they share `plotDrivesTable`.
  const plotDrivesFieldData = computePlotDrivesFieldBillSummaryData(groundworksData, itemLibraryData, ratesMapping);

  // Per-field Plot Pavings Bill Summary data — same proportional share contract
  // as Plot Drives. Same decimal-drift issue applies: thickness-dependent
  // fields (paving sub-base, MOT Type 1, etc.) have m³→m² rates that carry
  // more than 2 decimal places, so the generic `qty × rate` path produced a
  // value that didn't match the Bill Summary's 2-dp-truncated, rounded-qty
  // formula. Reading the BoQ amount per field and distributing proportionally
  // makes the Plot Matrix column total equal the Bill Summary grand total.
  const plotPavingsFieldData = computePlotPavingsFieldBillSummaryData(groundworksData, itemLibraryData, ratesMapping);

  // Scaffold uses a *category-level* consolidation (round(platformQty) ×
  // combinedRate, with combinedRate = Σ per-field rate, geotextile field
  // skipped when its checkbox is off) rather than per-field qty × rate, so we
  // cache per-category {quantityField, totalPlatformQty, billSummaryAmount}
  // and distribute proportionally across plot rows. Sum across rows ≡
  // `calculateScaffoldGrandTotal` per category — penny-perfect Sum Analysis
  // reconciliation.
  const scaffoldCategoryData = computeScaffoldCategoryBillSummaryData(groundworksData, itemLibraryData);


  // ─── PASS 1: Plot rows ───────────────────────────────────────────────────
  // Iterate the unioned plot identity (cutFills ∪ foundations ∪ scaffold plot
  // rows) so every plot that has data in any source table gets a row.
  plotIdentities.forEach((plotData) => {
    const row: PlotMatrixRow = {
      key: `plot|${plotData.plotNo}`,
      plotNo: plotData.plotNo || '',
      housetype: plotData.housetype || '',
      rowType: 'plot',
    };

    columns.forEach((column) => {
      const { stepName, categoryName, fieldNames } = column;

      // ─── Site Strip: Topsoil Disposal (Rule B — BoQ-matching) ────────────
      // Reverted from D1 Either/Or so the column sums equal the BoQ Section
      // 3.1 amount exactly. The previous Either/Or used different resource
      // codes per active field and could diverge from the BoQ's
      // `abs(Σ onsite − Σ offsite) × topsoilRate` formula.
      if (stepName === 'Cut & Fills' && categoryName === 'Site Strip - Topsoil Disposal') {
        const perPlot =
          soilBoQTotals && numberOfPlots > 0
            ? soilBoQTotals.topsoilAmount / numberOfPlots
            : 0;
        row[column.dataIndex] = { total: perPlot };
        return;
      }

      // ─── Cut/Fill: Disposal Of Surplus / Import of Deficit ───────────────
      // Deviation 3 — distribute the disposal or import amount equally
      // across plots (Rule C). Mutually exclusive: only one is non-zero,
      // driven by the sign of the foundations balance C22 (handled inside
      // `computeSoilBoQTotals`). The previous logic used
      // `subsoilManagement === 'Surplus' / 'Deficit'` as the picker, AND
      // summed 8 area pairs for the balance — both are now fixed at source.
      if (stepName === 'Cut & Fills' && categoryName === 'Cut/Fill - Disposal Of Surplus') {
        const perPlot =
          soilBoQTotals && numberOfPlots > 0
            ? soilBoQTotals.disposalAmount / numberOfPlots
            : 0;
        row[column.dataIndex] = { total: perPlot };
        return;
      }

      // ─── Private Drives to Binder - Sub Base: cross-sourced 6F2 ─────────
      // `fillToMakeUp6F2` was moved out of `Cut/Fill - Cut/Fill to Drives`
      // and now lives under `Private Drives to Binder - Sub Base`, but its
      // quantity is stored in `cutFillsTable` (not `plotDrivesTable`) and
      // its rate lives in `CUT_FILL_CATEGORIES["Cut & Fill"]`. Two reasons
      // the standard Plot Drives dispatch can't pick it up:
      //   1. `extractCategoryColumns` filters fields by `field in sampleRow`
      //      of the step's table, so 6F2 is dropped from `column.fieldNames`
      //      because no `plotDrivesTable` row carries that key.
      //   2. `computePlotDrivesCellAmount` only walks `plotDrivesFieldData`
      //      (built from `PLOT_DRIVES_CATEGORIES`), which has no entry for
      //      6F2.
      // We bypass both by handling this category explicitly: compute the
      // standard Plot Drives proportional share for the Plot Drives fields
      // that DO live in `plotDrivesTable`, then add the 6F2 cross-source
      // from `cutFillFieldData` (which carries 6F2 because `CUT_FILL_
      // CATEGORIES["Cut & Fill"]` still includes it). The result is that
      // the 6F2 amount that used to show up under Cut & Fill → Drives now
      // shows up here, with no double-count and no loss.
      if (
        stepName === 'Plot Drives' &&
        categoryName === 'Private Drives to Binder - Sub Base'
      ) {
        const plotKey = normalizePlotNoKey(plotData.plotNo);

        // Standard Plot Drives portion (same path as the generic dispatch).
        let cellTotal = 0;
        let drivesRowForBreakdown: any = undefined;
        if (plotDrivesFieldData.size > 0) {
          const drivesTable = transformPlotDrivesData(
            groundworksData.plotDrivesTable || [],
          );
          const drivesRow = drivesTable.find(
            (r: any) => normalizePlotNoKey(r?.plotNo) === plotKey,
          );
          if (drivesRow) {
            drivesRowForBreakdown = drivesRow;
            cellTotal += computePlotDrivesCellAmount(
              drivesRow,
              fieldNames,
              plotDrivesFieldData,
              cellCalcStrategy,
            );
          }
        }

        // Cross-source 6F2 from Cut & Fills, independent of plotDrivesTable
        // / fieldNames state — `cutFillFieldData.get('fillToMakeUp6F2')`
        // carries the Bill Summary amount, and the per-plot share comes
        // from this plot's `cutFillsTable.fillToMakeUp6F2`.
        //
        // Match ONLY plot rows (or legacy rows with no `plotType`). The
        // normalised plotNo key strips both `P-` and `G-` prefixes, so
        // without this filter a plot row whose own cut-fills entry has
        // zero/no data would silently inherit the garage's qty (when
        // the garage row comes first in the table). PASS 2 handles the
        // garage's contribution separately.
        let cfRowForBreakdown: any = undefined;
        if (cutFillFieldData.size > 0) {
          const cfRow = (groundworksData.cutFillsTable || []).find(
            (r: any) =>
              normalizePlotNoKey(r?.plotNo) === plotKey &&
              (r?.plotType === 'plot' || !r?.plotType),
          );
          if (cfRow) {
            cfRowForBreakdown = cfRow;
            cellTotal += computeCutFillCellAmount(
              cfRow,
              ['fillToMakeUp6F2'],
              cutFillFieldData,
              cellCalcStrategy,
            );
          }
        }

        // Build combined breakdown covering both Plot Drives fields and the
        // cross-sourced 6F2 field so the info modal shows all contributors.
        let breakdown: CellBreakdown | undefined;
        if (drivesRowForBreakdown) {
          breakdown = buildStandardCellBreakdown(drivesRowForBreakdown, fieldNames, plotDrivesFieldData, cellCalcStrategy);
        }
        if (cfRowForBreakdown) {
          const cf6F2Breakdown = buildStandardCellBreakdown(cfRowForBreakdown, ['fillToMakeUp6F2'], cutFillFieldData, cellCalcStrategy);
          if (cf6F2Breakdown.fields.length > 0) {
            breakdown = breakdown
              ? {
                  fields: [...breakdown.fields, ...cf6F2Breakdown.fields],
                  strategy: breakdown.strategy,
                  preUpliftTotal: breakdown.preUpliftTotal + cf6F2Breakdown.preUpliftTotal,
                }
              : cf6F2Breakdown;
          }
        }

        row[column.dataIndex] = { total: cellTotal, _breakdown: breakdown };
        return;
      }

      if (stepName === 'Cut & Fills' && categoryName === 'Cut/Fill - Import of Deficit') {
        const perPlot =
          soilBoQTotals && numberOfPlots > 0
            ? soilBoQTotals.importAmount / numberOfPlots
            : 0;
        row[column.dataIndex] = { total: perPlot };
        return;
      }

      // ─── Cut & Fills (non-soil categories) ───────────────────────────────
      // Per-plot proportional Bill Summary share — each cell shows the plot's
      // own qty × rate for the fields in this category, and the sum across
      // plots equals the Bill Summary section line. Site Strip / Cut-Fill
      // Foundations / Paths / Gardens / Drives all flow through this path so
      // the Plot Matrix column total ≡ BoQ section total. (The Either/Or
      // per-plot version from D2 broke that contract by switching to
      // unrounded qty × rate per plot — preserved as a comment for context.)
      if (
        stepName === 'Cut & Fills' &&
        CUT_FILL_NON_SOIL_PLOT_MATRIX_CATEGORIES.has(categoryName) &&
        cutFillFieldData.size > 0
      ) {
        const cfPlotKey = normalizePlotNoKey(plotData.plotNo);
        // Match ONLY plot rows (or legacy rows with no `plotType`).
        // `normalizePlotNoKey` strips both `P-` and `G-` prefixes, so without
        // this filter a plot row's `.find` collides with the garage row
        // sharing the same numeric key (e.g. P-1 vs G-1 both → "1") and the
        // plot inherits the garage's qty when the garage row appears first
        // in the table. PASS 2 emits the garage's own Cut & Fills cell.
        const cutFillRow = (groundworksData.cutFillsTable || []).find(
          (r: any) =>
            normalizePlotNoKey(r?.plotNo) === cfPlotKey &&
            (r?.plotType === 'plot' || !r?.plotType) &&
            r?.key !== 'totals' &&
            r?.plotNo !== 'TOTALS' &&
            r?.plotNo !== 'Total',
        );
        row[column.dataIndex] = {
          total: cutFillRow
            ? computeCutFillCellAmount(cutFillRow, fieldNames, cutFillFieldData, cellCalcStrategy)
            : 0,
          _breakdown: cutFillRow
            ? buildStandardCellBreakdown(cutFillRow, fieldNames, cutFillFieldData, cellCalcStrategy)
            : undefined,
        };
        return;
      }

      const tableMap: { [key: string]: any[] | undefined } = {
        'Cut & Fills': groundworksData.cutFillsTable,
        Foundations: groundworksData.foundationsTable,
        Drainage: groundworksData.drainageTableStandard || groundworksData.drainageTable,
        'Plot Drives': groundworksData.plotDrivesTable,
        'Plot Service': groundworksData.plotServiceTable,
        'Plot Pavings': groundworksData.plotPavingsTable,
        Scaffold: groundworksData.scaffoldTable,
        Garden: groundworksData.gardenTable,
        // POS Areas excluded - not plot-specific data
        'Ret & Screen Walls': groundworksData.retainingWallsTable,
        'Service Connections': groundworksData.plotServiceTable,
        // Geowall & Tobermore Walls excluded - not required in Plot Matrix
        'Private Drives Surface': groundworksData.plotDrivesTable,
        'Shared Drives Surface': groundworksData.plotDrivesTable,
      };

      let stepTable = tableMap[stepName];

      if ((stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') && stepTable) {
        stepTable = transformPlotDrivesData(stepTable);
      }

      // ─── Ret & Screen Walls — always run, even without a retainingWallsTable row ─
      // Geo-walls and Tobermore quantities live in `geoToberWallTable` and feed
      // into the same Plot Matrix Retaining Walls Foundation cell. Previously
      // this branch fell into the generic `stepPlotData` dispatch — if a plot
      // had geo-walls/tobermore quantities but NO retaining walls row, the
      // cell was forced to 0 and the geo contribution was silently dropped.
      // Now we always call `calculateCategoryTotalForRow`, passing either the
      // matched retainingWallsTable row or a synthetic `{ plotNo }` so the
      // geo/tobermore block (which keys off `geoToberWallTable.plotNo`) and
      // the screen-wall fields (zero when not present) still execute.
      if (stepName === 'Ret & Screen Walls') {
        const plotKey = normalizePlotNoKey(plotData.plotNo);
        const retRow = (groundworksData.retainingWallsTable || []).find(
          (r: any) => normalizePlotNoKey(r?.plotNo) === plotKey,
        ) || { plotNo: plotData.plotNo };
        const total = calculateCategoryTotalForRow(
          retRow,
          stepName,
          categoryName,
          fieldNames,
          groundworksData,
          ratesMapping,
          itemLibraryData,
          'plot',
          ohpPercent ?? 0,
        );
        const breakdown = categoryName === 'Screen Walls - upto DPC'
          ? buildScreenWallsFoundationBreakdown(
              retRow,
              groundworksData.retainingWallsTable || [],
              itemLibraryData,
              (groundworksData as any)?.wallsBuildUpConfig?.screenWallsLabel,
              (groundworksData as any)?.wallsBuildUpConfig?.eoPillarLabel,
            )
          : categoryName === 'Retaining Walls - Complete'
            ? buildRetainingWallsFoundationBreakdown(retRow, groundworksData, itemLibraryData)
            : undefined;
        row[column.dataIndex] = breakdown ? { total, _breakdown: breakdown } : { total };
        return;
      }

      // Special handling: Drainage / Plot Service / Service Connections use
      // Bill Summary grand total divided equally across plots (Rule C) so
      // each column total ≡ the corresponding Bill Summary section total.
      // Reverted from D8 — Pit Excavation is back on Rule C; the Either/Or
      // per-plot version diverged from the BoQ `calculateServiceConnections
      // GrandTotal` (which factors in the apartment rate multiplier).
      if ((stepName === 'Plot Service' || stepName === 'Service Connections' || stepName === 'Drainage') && stepTable?.length) {
        let billSummaryTotal = 0;

        if (stepName === 'Drainage') {
          if (categoryName === 'Plot Drainage - Foul') {
            billSummaryTotal = calculateDrainageFoulTotal(groundworksData, ratesMapping, itemLibraryData);
          } else if (categoryName === 'Plot Drainage - Storm') {
            billSummaryTotal = calculateDrainageStormTotal(groundworksData, ratesMapping, itemLibraryData);
          }
        } else if (stepName === 'Plot Service') {
          if (categoryName === 'Plot Services - Plot Services') {
            billSummaryTotal = calculatePlotServicesSectionTotal(groundworksData, ratesMapping, itemLibraryData);
          }
        } else if (stepName === 'Service Connections') {
          if (categoryName === 'Service Connections - Pit Excavation') {
            billSummaryTotal = calculateServiceConnectionsGrandTotal(groundworksData, ratesMapping, itemLibraryData);
          }
          // Service Connections - Pit Reinstatement: always 0 (no fields mapped).
        }

        const divisionTotal = numberOfPlots > 0 ? billSummaryTotal / numberOfPlots : 0;
        let cellBreakdown: CellBreakdown | undefined;
        if (billSummaryTotal > 0) {
          cellBreakdown = stepName === 'Drainage'
            ? buildDrainageCellBreakdown(groundworksData, categoryName, numberOfPlots, billSummaryTotal, ratesMapping, itemLibraryData)
            : buildDivisionCellBreakdown(billSummaryTotal, numberOfPlots);
        }
        row[column.dataIndex] = {
          total: divisionTotal,
          _breakdown: cellBreakdown,
        };
      } else if (stepTable?.length) {
        // For Foundations and Scaffold: match only plot rows (plotType === 'plot' or legacy rows with no plotType).
        // For all other steps: match by plotNo only (no garage rows exist in those tables).
        const isGarageSeparatedStep = stepName === 'Foundations' || stepName === 'Scaffold';

        // Compare via the normalised plotNo (strips `P-` / `G-`) so cutFillsTable's
        // raw plotNo ("1") matches foundationsTable's prefixed plotNo ("P-1").
        const plotKey = normalizePlotNoKey(plotData.plotNo);
        const stepPlotData = stepTable.find((stepRow) => {
          if (normalizePlotNoKey(stepRow.plotNo) !== plotKey) return false;
          if (isGarageSeparatedStep) {
            return stepRow.plotType === 'plot' || !stepRow.plotType;
          }
          return true;
        });

        // Reverted from D5 — Splash Course flows through the normal
        // Foundations proportional Bill Summary share path so the column
        // total matches the BoQ Foundations grand total exactly. The
        // Either/Or per-plot pick is gone; both `layFacingBrickwork` and
        // `supplyFacingBrickwork` contribute their proportional share like
        // any other Foundations field.

        // Foundations / Garden / Plot Drives: proportional Bill Summary share
        // per field — preserves per-plot variation AND makes the column total
        // match the Bill Summary grand total exactly. Falls back to the
        // per-row calc when no item library has been loaded (no rates).
        if (stepName === 'Foundations' && foundationsFieldData.size > 0) {
          row[column.dataIndex] = {
            total: stepPlotData
              ? computeFoundationsCellAmount(stepPlotData, fieldNames, foundationsFieldData, cellCalcStrategy)
              : 0,
            _breakdown: stepPlotData
              ? buildStandardCellBreakdown(stepPlotData, fieldNames, foundationsFieldData, cellCalcStrategy)
              : undefined,
          };
        } else if (stepName === 'Garden' && gardenFieldData.size > 0) {
          row[column.dataIndex] = {
            total: stepPlotData
              ? computeGardenCellAmount(stepPlotData, fieldNames, gardenFieldData, cellCalcStrategy)
              : 0,
            _breakdown: stepPlotData
              ? buildStandardCellBreakdown(stepPlotData, fieldNames, gardenFieldData, cellCalcStrategy)
              : undefined,
          };
        } else if (
          (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') &&
          plotDrivesFieldData.size > 0
        ) {
          row[column.dataIndex] = {
            total: stepPlotData
              ? computePlotDrivesCellAmount(stepPlotData, fieldNames, plotDrivesFieldData, cellCalcStrategy)
              : 0,
            _breakdown: stepPlotData
              ? buildStandardCellBreakdown(stepPlotData, fieldNames, plotDrivesFieldData, cellCalcStrategy)
              : undefined,
          };
        } else if (stepName === 'Plot Pavings' && plotPavingsFieldData.size > 0) {
          row[column.dataIndex] = {
            total: stepPlotData
              ? computePlotPavingsCellAmount(stepPlotData, fieldNames, plotPavingsFieldData, cellCalcStrategy)
              : 0,
            _breakdown: stepPlotData
              ? buildStandardCellBreakdown(stepPlotData, fieldNames, plotPavingsFieldData, cellCalcStrategy)
              : undefined,
          };
        } else if (stepName === 'Scaffold' && scaffoldCategoryData.size > 0) {
          // Use the consolidated BoQ formula via proportional share. Note
          // `rowData` here is `stepPlotData` (the matched scaffoldTable row);
          // the helper reads its quantityField (scaffoldPlatform /
          // loadingBay4x2m / stairAccess3x3m) to drive the per-plot share.
          row[column.dataIndex] = {
            total: stepPlotData
              ? computeScaffoldCellAmount(stepPlotData, categoryName, scaffoldCategoryData, cellCalcStrategy)
              : 0,
            _breakdown: stepPlotData
              ? buildScaffoldCellBreakdown(stepPlotData, categoryName, scaffoldCategoryData, cellCalcStrategy)
              : undefined,
          };
        } else {
          row[column.dataIndex] = {
            total: stepPlotData
              ? calculateCategoryTotalForRow(stepPlotData, stepName, categoryName, fieldNames, groundworksData, ratesMapping, itemLibraryData, 'plot', ohpPercent ?? 0)
              : 0,
          };
        }
      } else {
        row[column.dataIndex] = { total: 0 };
      }
    });

    rows.push(row);
  });

  // ─── PASS 2: Garage rows ─────────────────────────────────────────────────
  // Garages exist in Cut-Fills, Foundations and/or Scaffold tables.
  // Build a union of all unique garage plotNos from all three so a garage
  // that appears in only one is still represented. Cut-Fills garages carry
  // the foundation-related qty (`cutFounds` / `fillFounds`) — the only Cut/Fill
  // fields the UI exposes for garage rows — and must flow through to the
  // matrix `Cut/Fill - Cut/Fill to Foundations` column for those rows.
  const garageCutFillRows = (groundworksData.cutFillsTable || []).filter(
    (r) => r.plotType === 'garage' && r?.key !== 'totals'
  );
  const garageFoundationRows = (groundworksData.foundationsTable || []).filter(
    (r) => r.plotType === 'garage'
  );
  const garageScaffoldRows = (groundworksData.scaffoldTable || []).filter(
    (r) => r.plotType === 'garage'
  );

  // Collect unique garage plotNos preserving order: foundations first, then
  // scaffold-only, then cut-fills-only. Dedup by the normalised plotNo key
  // (strips `P-` / `G-`) so the same garage in multiple tables isn't treated
  // as separate rows.
  const garagePlotNos: string[] = [];
  const seenGaragePlotNoKeys = new Set<string>();

  [...garageFoundationRows, ...garageScaffoldRows, ...garageCutFillRows].forEach((r) => {
    const plotNo = r.plotNo || '';
    const key = normalizePlotNoKey(plotNo);
    if (!seenGaragePlotNoKeys.has(key)) {
      seenGaragePlotNoKeys.add(key);
      garagePlotNos.push(plotNo);
    }
  });

  if (garagePlotNos.length > 0) {
    garagePlotNos.forEach((garagePlotNo) => {
      const garageKey = normalizePlotNoKey(garagePlotNo);
      const garageFoundationData = garageFoundationRows.find((r) => normalizePlotNoKey(r.plotNo) === garageKey);
      const garageScaffoldData = garageScaffoldRows.find((r) => normalizePlotNoKey(r.plotNo) === garageKey);
      const garageCutFillData = garageCutFillRows.find((r) => normalizePlotNoKey(r.plotNo) === garageKey);

      // Derive housetype from whichever source has the row
      const housetype = (garageFoundationData || garageScaffoldData || garageCutFillData)?.housetype || '';

      const row: PlotMatrixRow = {
        key: `garage|${garagePlotNo}`,
        plotNo: garagePlotNo,
        housetype,
        rowType: 'garage',
      };

      columns.forEach((column) => {
        const { stepName, categoryName, fieldNames } = column;

        if (stepName === 'Foundations') {
          // Same proportional Bill Summary distribution as plot rows.
          // `foundationsFieldData` includes both plot and garage qty in its
          // `totalQty`, so garage rows take their proportional share too —
          // sum across plot + garage rows = Foundations Bill Summary grand
          // total. Fallback to per-row calc when no item library is loaded.
          if (foundationsFieldData.size > 0) {
            row[column.dataIndex] = {
              total: garageFoundationData
                ? computeFoundationsCellAmount(garageFoundationData, fieldNames, foundationsFieldData, cellCalcStrategy)
                : 0,
              _breakdown: garageFoundationData
                ? buildStandardCellBreakdown(garageFoundationData, fieldNames, foundationsFieldData, cellCalcStrategy)
                : undefined,
            };
          } else {
            row[column.dataIndex] = {
              total: garageFoundationData
                ? calculateCategoryTotalForRow(garageFoundationData, stepName, categoryName, fieldNames, groundworksData, ratesMapping, itemLibraryData, 'garage', ohpPercent ?? 0)
                : 0,
            };
          }
        } else if (stepName === 'Scaffold') {
          // `scaffoldCategoryData.totalPlatformQty` includes BOTH plot and
          // garage rows (because `generateBOQSummaryWithCategories` sums the
          // full `scaffoldTable`), so distributing by `(garageQty / totalQty)`
          // gives the garage's correct proportional share. Together with the
          // plot rows above, the column sum equals the BoQ Scaffold
          // consolidated total.
          if (scaffoldCategoryData.size > 0) {
            row[column.dataIndex] = {
              total: garageScaffoldData
                ? computeScaffoldCellAmount(garageScaffoldData, categoryName, scaffoldCategoryData, cellCalcStrategy)
                : 0,
              _breakdown: garageScaffoldData
                ? buildScaffoldCellBreakdown(garageScaffoldData, categoryName, scaffoldCategoryData, cellCalcStrategy)
                : undefined,
            };
            return;
          }
          row[column.dataIndex] = {
            total: garageScaffoldData
              ? calculateCategoryTotalForRow(garageScaffoldData, stepName, categoryName, fieldNames, groundworksData, ratesMapping, itemLibraryData, 'garage', ohpPercent ?? 0)
              : 0,
          };
        } else if (stepName === 'Cut & Fills') {
          // CutFills UI exposes only `cutFounds` / `fillFounds` inputs for
          // garage rows (paths/garden/drives/site-strip inputs are hidden),
          // so in practice only the `Cut/Fill - Cut/Fill to Foundations`
          // category produces a non-zero cell here — but route every Cut &
          // Fills non-soil category through the same proportional share path
          // for symmetry with plot rows. `cutFillFieldData.totalQty` already
          // sums plot + garage qty (the BoQ pipeline walks the whole
          // cutFillsTable without filtering by plotType), so the column sum
          // across plot + garage rows equals the Cut & Fill Bill Summary
          // grand total. Soil-management categories stay 0 for garages —
          // they distribute via `soilBoQTotals / numberOfPlots` to plot rows
          // only.
          if (
            CUT_FILL_NON_SOIL_PLOT_MATRIX_CATEGORIES.has(categoryName) &&
            cutFillFieldData.size > 0
          ) {
            row[column.dataIndex] = {
              total: garageCutFillData
                ? computeCutFillCellAmount(garageCutFillData, fieldNames, cutFillFieldData, cellCalcStrategy)
                : 0,
              _breakdown: garageCutFillData
                ? buildStandardCellBreakdown(garageCutFillData, fieldNames, cutFillFieldData, cellCalcStrategy)
                : undefined,
            };
          } else {
            row[column.dataIndex] = {
              total: garageCutFillData
                ? calculateCategoryTotalForRow(garageCutFillData, stepName, categoryName, fieldNames, groundworksData, ratesMapping, itemLibraryData, 'garage', ohpPercent ?? 0)
                : 0,
            };
          }
        } else {
          // All other valuation stages are not applicable to garages
          row[column.dataIndex] = { total: 0 };
        }
      });

      rows.push(row);
    });
  }

  // Apply the prelims + OH&P uplift to every `{ total: number }` cell on
  // every row before returning. Every step (Site Strip, Cut/Fill, Foundations,
  // Scaffold, Drainage, Plot Services, Plot Pavings, Garden, Ret & Screen
  // Walls, Plot Drives, etc.) writes its cells in `{ total: number }` form, so
  // this single pass guarantees the uplift is applied uniformly — replacing
  // the previous external `applyPlotMatrixUplift` call from the caller. A
  // factor of exactly 1 still runs the pass so per-cell totals are normalised
  // to 2dp (matches the `applyPlotMatrixUplift` rounding contract that the
  // column-total strip and row-total column rely on).
  // Build dataIndex → section key map from the columns array so
  // `applyPlotMatrixUplift` can do per-section rather than whole-matrix
  // reconciliation when `boqStepTargets` is supplied.
  const columnSectionMap = new Map<string, string>();
  columns.forEach((col) => {
    columnSectionMap.set(col.dataIndex, getColumnSectionKey(col.stepName));
  });

  return applyPlotMatrixUplift(rows, upliftFactor, boqCanonicalGrandTotal, cellCalcStrategy, columnSectionMap, boqStepTargets) as PlotMatrixRow[];
};

/**
 * Calculate row total
 */
export const calculateRowTotal = (row: PlotMatrixRow): number => {
  let total = 0;

  Object.keys(row).forEach((key) => {
    if (key !== "key" && key !== "plotNo" && key !== "housetype") {
      const cellData = row[key];
      if (cellData && typeof cellData === "object" && "total" in cellData) {
        total += cellData.total || 0;
      }
    }
  });

  // Round to 2dp so summing many 2dp-rounded cells doesn't accumulate
  // sub-penny float residuals (e.g. 12 cells of 0.01 → 0.12000000000000001).
  // Pair with `applyPlotMatrixUplift`'s 2dp cell rounding and the matching
  // 2dp round on column totals in `PlotMatrix.tsx:renderSummaryRow` so the
  // row-total column, column-total strip and grand-total cell all agree.
  return parseFloat(total.toFixed(2));
};